import angular from 'angular';
import { QueryableWorkflow } from './queryable.workflow';
import {
  ActorTypes,
  ApprovalTaskOptions,
  DynamicActorTypeNames,
  FormFieldType,
  LookupSpecialFieldLabels,
  NodeCategory,
  TableCellType
} from '../flowingly.services/flowingly.constants';
import { Services } from '../@types/services';
import { ModelerValidation } from './@types/services';
import { BpmnModeler } from '@Shared.Angular/flowingly.bpmn.modeler/@types/services';
import { DiagramKey } from '@Shared.Angular/flowingly.bpmn.modeler/flowingly.bpmn.constants';
import { IModelerNodeLink, IWorkflow } from '@Shared.Angular/@types/workflow';
import IModelerNode from '@Shared.Angular/@types/modelerNode';
import IFormField from '@Shared.Angular/@types/core/contracts/queryModel/card/formField';
import IStepNode from '@Shared.Angular/@types/core/contracts/queryModel/modeler/stepNode';
import {
  PublicFormType,
  PublicFormTypeLabels
} from '@Shared.Angular/@types/core/contracts/constants/publicFormType';
import { LookupSpecialFields } from '@Shared.Angular/@types/core/contracts/queryModel/workflow/lookupSpecialFields';
import { ExternalUserValidationResult } from '@Shared.Angular/@types/core/contracts/queryModel/user/ExternalUserValidationResult';
import IRunnerCard from '@Shared.Angular/@types/core/contracts/queryModel/card/runnerCard';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import FieldDataSource from '@Shared.Angular/@types/fieldDataSource';
import { EmptyGuid } from '@Shared.Angular/@types/guid';
import { detectLoops } from './workflow.loop-detection';
import { getUnreachableNodeKeys } from './workflow.reachability';

/**
 * @ngdoc service
 * @name modelerValidationService
 * @module flowingly.modeler.validation
 *
 * @description A service for validating Flow Models
 */

interface IValidationContext {
  diagram: any;
  flow: any;
  model: any;
  allCustomDbs: any;
  allModelNodes: any;

  /*  equivalent to
   *     context.queryable.goNodeData.toList()
   *     mainDiagram.model.nodeDataArray
   */
  allLinks: any;

  activityNodes: any;
  componentNodes: any;
  /*equivalent to
   *      getNodes() [DEPRECATED]
   *      context.queryable.nodes.toList()
   */
  workflowNodes: any;
  publishedProcessMapComponents: any;
  publishedWorkflowComponents: any;
  queryable: QueryableWorkflow;
}

interface IValidationError {
  message: string;
  isServerError?: boolean;
}

interface IValidationSettings {
  stepTypeNumberSupportedForIPaaS: string[];
  validTaskTypesForIpaasIntegration: ActivityTaskType[];
}

angular
  .module('flowingly.modeler.validation')
  .factory('modelerValidationService', modelerValidationService);

modelerValidationService.$inject = [
  '$http',
  'lodashService',
  'flowinglyGatewayService',
  'flowinglyActivityService',
  'flowinglyModelUtilityService',
  'rulesetParserService',
  'rulesetValidationService',
  'rulesetService',
  'validationService',
  'guidService',
  'intercomService',
  '$q',
  'componentApiService',
  'flowinglyConstants',
  'jQuery',
  'BpmnQueryableWorkflow',
  'modelerValidationErrorsService',
  'BpmnDiagramService',
  'userApiService',
  'modelerApiService',
  'customdbService'
];

function modelerValidationService(
  $http: angular.IHttpService,
  lodash: Lodash,
  flowinglyGatewayService: Services.FlowinglyGatewayService,
  flowinglyActivityService: Services.FlowinglyActivityService,
  flowinglyModelUtilityService: Services.FlowinglyModelUtilityService,
  rulesetParserService: ModelerValidation.RulesetParserService,
  rulesetValidationService: ModelerValidation.RuleSetValidationService,
  rulesetService: ModelerValidation.RuleSetService,
  validationService: Services.ValidationService,
  guidService: Services.GuidService,
  intercomService: Services.IntercomService,
  $q: angular.IQService,
  componentApiService: Services.ComponentApiService,
  flowinglyConstants: Services.FlowinglyConstants,
  $: JQueryStatic,
  BpmnQueryableWorkflow: ModelerValidation.BpmnQueryableWorkflow,
  modelerValidationErrorsService: ModelerValidation.ModelerValidationErrorsService,
  BpmnDiagramService: BpmnModeler.BpmnDiagramService,
  userApiService: Services.UserApiService,
  modelerApiService: Services.ModelerApiService,
  customdbService: Services.CustomdbService
) {
  let errors = [] as IValidationError[]; // tsk tsk bad man. bad bad man. - cassey
  let workflow: IWorkflow;
  let appConfig: Services.APP_CONFIG;
  let allLinks;
  let isComponent = false;
  let validationSettings: IValidationSettings;
  const service = {
    validateWorkFlow: validateWorkFlow,
    validateWorkflowComponent: validateWorkflowComponent,
    validateProcessMap: validateProcessMap,
    validateWorkflowPassXssCheck: validateWorkflowPassXssCheck
  };

  return service;

  function validateProcessMap(flow, config) {
    modelerValidationErrorsService.resetAllErrors();

    workflow = flow;
    errors = [];
    appConfig = config;

    validatePublicFormExistsInProcessMap();

    const diagram = BpmnDiagramService.getDiagram(DiagramKey.MAIN);

    console.log('Start validateProcessMap updateAllTargetBindings');

    // We update the graph to remove and add the errors
    diagram.startTransaction('ValidateProcessMap_Update');
    diagram.updateAllTargetBindings();
    diagram.commitTransaction('ValidateProcessMap_Update');

    console.log('Commit validateProcessMap updateAllTargetBindings');

    return $q.when(errors);
  }

  async function validateWorkFlow(flow, config, settings) {
    modelerValidationErrorsService.resetAllErrors();
    const getCustomDBPromise = customdbService.getAllDatabases();

    const result = await $q.all([
      getCustomDBPromise,
      componentApiService.getProcessMapComponents(),
      componentApiService.getWorkflowComponents()
    ]);

    const allCustomDbs = result[0];
    const publishedProcessMapComponents = result[1];
    const publishedWorkflowComponents = result[2];

    workflow = flow;
    errors = [];
    appConfig = config;
    validationSettings = settings;
    const model = JSON.parse(flow.FlowSchema);
    const allModelNodes = getAllModelNodes(model);
    allLinks = getAllModelLinks(model);
    const activityNodes =
      flowinglyModelUtilityService.getActivityNodes(allModelNodes);
    const componentNodes =
      flowinglyModelUtilityService.getComponentNodes(allModelNodes);

    const context = {
      // shitty solution but cleaner than what we had before
      diagram: BpmnDiagramService.getDiagram(DiagramKey.MAIN), // this is the actual go js diagram

      flow,
      model,
      allCustomDbs,
      allModelNodes, // equivalent to
      //      context.queryable.goNodeData.toList()
      //      mainDiagram.model.nodeDataArray
      allLinks,

      activityNodes,
      componentNodes,
      workflowNodes: flow.Nodes, // equivalent to
      //      getNodes() [DEPRECATED]
      //      context.queryable.nodes.toList()
      publishedProcessMapComponents,
      publishedWorkflowComponents,

      queryable: new BpmnQueryableWorkflow.QueryableWorkflow(flow)
    } as IValidationContext;
    validateModelNodes(context, validationSettings);
    validateEachComponentNode(context);
    validateCustomValidation(context);
    validatePublicForm(context);
    validateContentInWorkflowNodes(context);

    const linksByFromKey = allLinks.reduce(
      (acc: Map<number, IModelerNodeLink[]>, link: IModelerNodeLink) => {
        if (!acc.has(link.from)) {
          acc.set(link.from, []);
        }
        acc.get(link.from).push(link);
        return acc;
      },
      new Map<number, IModelerNodeLink[]>()
    ) as Map<number, IModelerNodeLink[]>;
    const nodesByKey = new Map<number, IModelerNode>(
      allModelNodes.map((n) => [n.key, n])
    );

    validateBacklinks(allModelNodes, allLinks, nodesByKey);
    if (appConfig.enableFlowModelLoopValidation) {
      validateLoops(allModelNodes, workflow.Nodes, nodesByKey, linksByFromKey);
    }
    validateNodesReachableFromStart(allModelNodes, allLinks);

    // @TODO whoever works on this code file just know that the goal of this
    // block of code is for us to encapsulate the errors and remove the global-like
    // accessing of the errors. This is so we con convert it to separate classes,
    // and eventually make it unit testable.
    //                                               - Cassey
    validateNodeDepths(context, errors);
    validateStepRules(flow.Nodes, allModelNodes);
    checkForDivergePathsWithoutSteps(allModelNodes, errors, linksByFromKey);
    validateFieldConditions(context);

    if (context.queryable._rootQueryableNode != null) {
      const firstNode = flowinglyModelUtilityService.getFirstModelNode(
        allModelNodes,
        allLinks
      );
      for (let i = 0; i < context.allModelNodes.length; i++) {
        // validateNodeFields should be moved to this pattern slowly.
        const modelNode = context.allModelNodes[i] as IModelerNode; // a.k.a goNodeData
        const workflowNode = context.queryable.nodes.getByIndex(
          'ModelerNodeId',
          modelNode.id
        ) as IStepNode; // a.k.a workflowNode, node

        const nodeLinks = getLinks(model, modelNode);
        const newContext = { ...context, modelNode, nodeLinks };

        // validate links or connectors
        validateThatStartNodeOnlyHasFromLinks(nodeLinks);
        validateThatEndNodeOnlyHasToLinks(nodeLinks);
        validateThatNodeDoesNotHaveMultipleFromLinks(nodeLinks, modelNode);
        validateNodeHasToConnector(nodeLinks, modelNode);
        validateNodeHasFromConnector(nodeLinks, modelNode);
        if (
          workflowNode?.IsFirstNode &&
          modelNode.taskType === ActivityTaskType.CUSTOM_EMAIL
        ) {
          validateDynamicActorAfterInitialEmail(
            modelNode,
            allModelNodes,
            allLinks
          );
        }

        // node specific validations
        switch (modelNode.category) {
          case flowinglyConstants.nodeCategory.EXCLUSIVE_GATEWAY:
            validateExclusiveGateWay(model.nodeDataArray, modelNode, nodeLinks);
            validateDecisionField(
              model.nodeDataArray,
              model.linkDataArray,
              modelNode
            );
            break;

          case flowinglyConstants.nodeCategory.DIVERGE_GATEWAY:
            validateDivergeGateWay(
              allModelNodes,
              allLinks,
              modelNode,
              nodeLinks
            );
            break;

          case flowinglyConstants.nodeCategory.CONVERGE_GATEWAY:
            validateConvergeGateWay(
              allModelNodes,
              allLinks,
              modelNode,
              nodeLinks
            );
            break;

          case flowinglyConstants.nodeCategory.ACTIVITY:
            validateDynamicActor(modelNode, allModelNodes, allLinks);
            validateHasValidActor(modelNode);

            validateNodeFields(firstNode, modelNode, allCustomDbs); // @TODO merge these 2
            validateNodeFieldsV2(newContext, errors); // @TODO merge these 2

            // @TODO move loops of formfields here
            for (const formElement of (workflowNode.Card as IRunnerCard)
              .formElements) {
              validatePreviousField(
                modelNode,
                workflowNode,
                context.workflowNodes,
                formElement
              );

              switch (formElement.type) {
                case 'lookup':
                  validateLookupField(
                    modelNode,
                    formElement.lookupConfig,
                    'Lookup',
                    formElement.displayName,
                    allCustomDbs,
                    errors,
                    firstNode
                  );
                  break;
                case 'instruction':
                  validateInstructionField(
                    formElement,
                    context,
                    workflowNode,
                    modelNode,
                    errors
                  );
                  break;
                case FormFieldType.RECAPTCHA:
                  validateRecaptchaField(
                    formElement,
                    workflowNode,
                    modelNode,
                    errors
                  );
                  break;
                default:
                  break;
              }
            }

            break;
          case 'event':
            break;
          default:
            console.error(
              'Found unknown category type supplied to node',
              modelNode
            );
            break;
        }

        // sequential approval and parallel approval
        if (flowinglyModelUtilityService.isMultipleApprovalNode(modelNode)) {
          //@TODO is this also an activity?
          validateMultipleApproval(modelNode);
        }
      }
    }

    // We update the graph to remove and add the errors
    const diagram = BpmnDiagramService.getDiagram(DiagramKey.MAIN);

    console.log('Start validateWorkFlow updateAllTargetBindings');

    // We update the graph to remove and add the errors
    diagram.startTransaction('ValidateWorkFlow_Update');
    diagram.updateAllTargetBindings();
    diagram.commitTransaction('ValidateWorkFlow_Update');

    console.log('Commit validateWorkFlow updateAllTargetBindings');

    if (errors.length > 0) {
      return $q.when(errors);
    } else {
      return validateWorkflowSchema();
    }
  }

  function validateNodeFieldsV2(context, errors) {
    const { modelNode, queryable } = context;

    if (modelNode.category === 'activity') {
      const node = queryable.nodes.getByIndex('ModelerNodeId', modelNode.id);
      const card = node.Card;
      const formElements = card.formElements;

      for (let i = 0; i < formElements.length; i++) {
        const formElement = formElements[i];

        const goNodeData = queryable.goNodeData
          .toQueryable()
          .limit(1)
          .where((goNode) => goNode.id == modelNode.id)
          .first();

        if (formElement.defaultPreviousStepId) {
          let isValid;
          try {
            isValid = validateIfPreviousFieldIsAccessible({
              ...context,
              goNodeData,
              formElement
            });
          } catch (err) {
            isValid = false;
            console.error(err);
          }
          if (!isValid) {
            const error = {
              message: `${goNodeData.text} has the field "${formElement.displayName}" referencing a step it has no access to.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          }
        }
      }
    }
  }

  /**
   * This is an expensive process of iterating through the nodes, but atleast we are using
   * the gojs built in node finder isntead of our own version of the node finder.
   */
  function validateIfPreviousFieldIsAccessible({
    goNodeData,
    formElement,
    diagram
  }): boolean {
    const startingNode = diagram.findNodeForKey(goNodeData.key);
    const foundGoNode = flowinglyModelUtilityService.findPreviousUntil(
      startingNode,
      (goJsNode) => {
        return goJsNode.data.id == formElement.defaultPreviousStepId;
      },
      BpmnDiagramService.getDiagram(DiagramKey.MAIN)
    );

    return !!foundGoNode;
  }

  // Depth increases by 1 at each diverge gateway, and decreases by 1 at each converge gateway.
  // So non-diverge/merge nodes should only have parents (i.e. prior nodes) with the same depth.
  function validateNodeDepths({ queryable }: IValidationContext, errors) {
    const relevantNodeCategories = [
      NodeCategory.ACTIVITY,
      NodeCategory.EXCLUSIVE_GATEWAY,
      NodeCategory.COMPONENT
    ];
    queryable.queryableNodes
      .toQueryable()
      .where(
        (node) =>
          relevantNodeCategories.includes(node.goNodeData.category) &&
          node.parents.some((parentNode) => node.depth != parentNode.depth)
      )
      .toList()
      .forEach(({ goNodeData }) => {
        const error = {
          message: `${goNodeData.text} has a connector coming from a different path following a diverge.`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
      });
  }

  function validateWorkflowComponent(flow, config) {
    modelerValidationErrorsService.resetAllErrors();

    workflow = flow;
    errors = [];
    appConfig = config;
    isComponent = true;
    validateAdditionalRulesForWorkflowComponent(flow);

    if (errors.length > 0) {
      const diagram = BpmnDiagramService.getDiagram(DiagramKey.MAIN);

      console.log('Start validateWorkflowComponent updateAllTargetBindings');

      // // We update the graph to remove and add the errors
      diagram.startTransaction('ValidateWorkflowComponent_Update');
      diagram.updateAllTargetBindings();
      diagram.commitTransaction('ValidateWorkflowComponent_Update');

      console.log('Commit validateWorkflowComponent updateAllTargetBindings');

      return $q.when(errors);
    }

    return validateWorkFlow(flow, config, null);
  }

  function validateWorkflowSchema() {
    return modelerApiService
      .getValidationErrors(workflow.FlowSchema, workflow.Id)
      .then((response) => {
        for (const error of response.data) {
          errors.push({ message: error, isServerError: true });
        }
        if (errors.length > 0) {
          intercomService.trackEvent(
            'Flow Model Failed Server Side Validation',
            {
              'flow name': workflow.Name
            }
          );
        }
        return errors;
      });
  }

  function validateModelNodes(
    { allModelNodes },
    validationSettings: IValidationSettings
  ) {
    validateAtleastOneStep(allModelNodes);
    validateStartObject(allModelNodes);
    validateEndObject(allModelNodes);
    validateUniqueNodeId(allModelNodes);
    validateNodeNames(allModelNodes);
    validateNodesForIpaasIntegration(allModelNodes, validationSettings);
  }

  function validateEachComponentNode({
    componentNodes,
    allModelNodes,
    allLinks,
    publishedProcessMapComponents,
    publishedWorkflowComponents
  }) {
    lodash.forEach(componentNodes, function (componentNode) {
      validateComponentNode(
        componentNode,
        allModelNodes,
        allLinks,
        publishedProcessMapComponents,
        publishedWorkflowComponents
      );
    });
  }

  function validatePublicForm({ allModelNodes, allLinks }) {
    const publicFormNodes = getPublicFormNodes();
    if (publicFormNodes == null || publicFormNodes.length === 0) {
      return;
    }
    // Validate all instruction fields in all the public form.
    for (const publicFormNode of publicFormNodes) {
      for (const formElement of (publicFormNode.Card as IRunnerCard)
        .formElements) {
        if (
          formElement.type === 'instruction' &&
          containsStepOrFlowAttributeVariables(formElement)
        ) {
          const error: IValidationError = {
            message:
              `'${publicFormNode.StepName}' contains variables in an instruction field, ` +
              'variables within instruction fields are not supported on Public Forms, ' +
              'please remove these in order to publish.'
          };
          const modelNodePublicForm = allModelNodes.find(
            (modelNode) => modelNode.id === publicFormNode.ModelerNodeId
          );
          modelerValidationErrorsService.addError(
            modelNodePublicForm.key,
            error
          );
          errors.push(error);
          break;
        }
      }
    }

    validatePublicFormSubject(publicFormNodes, allModelNodes);

    const publicFormNodesViaActor = getPublicFormNodesViaActorName();
    if (publicFormNodesViaActor.length > 1 || !publicFormNodes[0].IsFirstNode) {
      const error: IValidationError = {
        message:
          'There can only be one Public Form, and it must be the first Step of the Flow Model.'
      };

      // special case for redbox validations : color all public forms red
      publicFormNodesViaActor.forEach((goNodeData) => {
        if (publicFormNodes[0].ModelerNodeId != (goNodeData as any).data) {
          modelerValidationErrorsService.addError(goNodeData.key, error);
        }
      });

      errors.push(error);
      return;
    }

    const publicFormModelNode = allModelNodes.find(
      (modelNode) => modelNode.id === publicFormNodes[0].ModelerNodeId
    );

    const activityNodesAfterPublicForm =
      flowinglyModelUtilityService.getNextActivityNodesForNodeRecursive(
        publicFormModelNode.key,
        allModelNodes,
        allLinks,
        null,
        false
      );

    validateAssignedActorAndApproverForNodesAfterPublicForm(
      publicFormModelNode,
      activityNodesAfterPublicForm
    );
  }

  function validatePublicFormSubject(publicFormNodes, allModelNodes) {
    if (appConfig.enableCustomPublicFormSubjects) {
      for (const publicFormNode of publicFormNodes) {
        const modelerNode = allModelNodes.find(
          (modelNode) => modelNode.id === publicFormNode.ModelerNodeId
        );
        if (
          modelerNode.flowSubject == null ||
          modelerNode.flowSubject.replaceAll(' ', '') === ''
        ) {
          const error: IValidationError = {
            message: `Workflow Subject field of '${publicFormNode.StepName}' cannot be blank.`
          };
          modelerValidationErrorsService.addError(modelerNode.key, error);
          errors.push(error);
          break;
        }
        const invalidVariables = getInvalidTemplateVariablesForNode(
          modelerNode.flowSubject,
          publicFormNode
        );
        if (invalidVariables) {
          invalidVariables.forEach((variable) => {
            const error: IValidationError = {
              message: `'${publicFormNode.StepName}' contains invalid Step field variable '${variable}' in its Workflow Subject.`
            };
            modelerValidationErrorsService.addError(modelerNode.key, error);
            errors.push(error);
          });
        }
      }
    }
  }

  function containsStepOrFlowAttributeVariables(formElement) {
    const instructionFieldValue = formElement.value;
    if (instructionFieldValue != null) {
      const template = htmlDecode(instructionFieldValue);
      const stepVariableRegex = /{step./g;
      const flowVariableRegex = /{flow./g;

      return (
        stepVariableRegex.test(template) || flowVariableRegex.test(template)
      );
    }
    return false;
  }

  function validatePublicFormExistsInProcessMap() {
    const publicFormNodes = getPublicFormNodes();
    if (publicFormNodes && publicFormNodes.length > 0) {
      const error: IValidationError = {
        message: `A Public Form can not be used in a Process Map.`
      };

      // special case for redbox validations : color all public forms red
      getPublicFormNodesViaActorName().forEach((goNodeData) =>
        modelerValidationErrorsService.addError(goNodeData.key, error)
      );

      errors.push(error);
    }

    return;
  }

  function getPublicFormNodesViaActorName() {
    return (
      BpmnDiagramService.getDiagram(DiagramKey.MAIN).model
        .nodeDataArray as IModelerNode[]
    ).filter((goNodeData) => goNodeData.actorName == 'Public User');
  }

  function getPublicFormNodes() {
    const publicFormNode = getNodes().filter((n) => {
      return (
        n.IsPublicForm &&
        ((n as any).isDeleted === undefined || (n as any).isDeleted === false)
      );
    });

    return publicFormNode;
  }

  function validateContentInWorkflowNodes({
    workflowNodes,
    allModelNodes,
    allLinks
  }) {
    validateSelectApproversNode(workflowNodes, allModelNodes, allLinks);
    validateCustomEmailNode(workflowNodes, allModelNodes, allLinks);
  }

  function validateComponentNode(
    modelNode,
    allModelNodes,
    allLinks,
    publishedProcessMapComponents,
    publishedWorkflowComponents
  ) {
    if (!modelNode.componentSchemaId) {
      const error: IValidationError = {
        message: `A Component needs to be selected for '${modelNode.text}'.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    } else if (
      publishedProcessMapComponents.find(
        (c) => c.ComponentSchemaId === modelNode.componentSchemaId
      )
    ) {
      const error: IValidationError = {
        message: `The Process Map Component '${modelNode.text}' cannot be used in a Workflow.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    } else if (
      !publishedWorkflowComponents.find(
        (c) => c.ComponentSchemaId === modelNode.componentSchemaId
      )
    ) {
      const error: IValidationError = {
        message: `The Component used for '${modelNode.text}' is invalid.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }

    validateDynamicActorNodeAfterComponent(modelNode, allModelNodes, allLinks);
  }

  function validateDynamicActorNodeAfterComponent(
    modelNode,
    allModelNodes,
    allLinks
  ) {
    const nextNodes =
      flowinglyModelUtilityService.getNextActivityNodesForNodeRecursive(
        modelNode.key,
        allModelNodes,
        allLinks,
        null,
        false
      );
    if (!nextNodes || nextNodes.length === 0) return;

    const node = nextNodes.find(
      (n) =>
        n.actorType === ActorTypes.DYNAMIC &&
        n.actor === DynamicActorTypeNames.SELECT_DYNAMIC_ACTORS
    );
    if (node) {
      const error: IValidationError = {
        message: `'${node.text}' cannot be assigned to a dynamic Actor when preceded by a Component.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
  }

  function validateAdditionalRulesForWorkflowComponent(flow) {
    const model = JSON.parse(flow.FlowSchema);
    const allModelNodes = getAllModelNodes(model);
    componentDoesNotContainOtherComponents(allModelNodes);
    componentDoesNotContainPublicForm();
  }

  function componentDoesNotContainOtherComponents(nodes) {
    lodash.forEach(nodes, function (node) {
      if (node.category === flowinglyConstants.nodeCategory.COMPONENT) {
        const error: IValidationError = {
          message: `Component '${node.text}' cannot be used inside a Component.`
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
    });
  }

  function componentDoesNotContainPublicForm() {
    const publicFormNodes = getPublicFormNodes();
    if (publicFormNodes && publicFormNodes.length > 0) {
      const error: IValidationError = {
        message: 'Public form cannot be used inside a Component.'
      };

      // special case for redbox validations : color all public forms red
      publicFormNodes.forEach((node) => {
        const goNodeData = (
          BpmnDiagramService.getDiagram(DiagramKey.MAIN).model
            .nodeDataArray as IModelerNode[]
        ).find((n) => n.id == node.ModelerNodeId);
        modelerValidationErrorsService.addError(goNodeData.key, error);
      });

      errors.push(error);
    }
  }

  function validateNodeNames(nodes) {
    lodash.forEach(nodes, function (node) {
      if (
        node.category !== flowinglyConstants.nodeCategory.EVENT &&
        node.category !== flowinglyConstants.nodeCategory.POOL &&
        node.category !== flowinglyConstants.nodeCategory.LANE &&
        (node.text.toLowerCase() === 'start' ||
          node.text.toLowerCase() === 'end')
      ) {
        const error: IValidationError = {
          message: "Nodes cannot be named 'Start' or 'End'."
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
    });
    const names = [];
    lodash.forEach(nodes, function (node) {
      if (
        node.category === flowinglyConstants.nodeCategory.EVENT ||
        node.category === flowinglyConstants.nodeCategory.CONVERGE_GATEWAY ||
        node.category === flowinglyConstants.nodeCategory.POOL ||
        node.category === flowinglyConstants.nodeCategory.LANE
      ) {
        return;
      }
      if (node.text === undefined || node.text.trim() === '') {
        if (node.refSequence) {
          const error: IValidationError = {
            message: 'Node ' + node.refSequence + ' requires a name.'
          };
          modelerValidationErrorsService.addError(node.key, error);
          errors.push(error);
        } else {
          const error: IValidationError = {
            message: 'A Node requires a name.'
          };
          modelerValidationErrorsService.addError(node.key, error);
          errors.push(error);
        }
      } else if (lodash.indexOf(names, node.text.toLowerCase()) === -1) {
        names.push(node.text.toLowerCase());
      } else {
        const error: IValidationError = {
          message: `No Nodes can have the same name: '${node.text.toLowerCase()}'.`
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
    });
  }

  function validateHasValidActor(modelNode: IModelerNode) {
    if (
      modelNode.taskType === ActivityTaskType.CUSTOM_EMAIL ||
      modelNode.taskType === ActivityTaskType.COMPONENT ||
      modelNode.taskType === ActivityTaskType.PARALLEL_APPROVAL ||
      modelNode.taskType === ActivityTaskType.SEQUENTIAL_APPROVAL
    ) {
      // Component & CustomEmail have no actor
      // Sequential & Parallel Approval are checked in validateMultipleApproval
      return;
    }
    if (!modelNode.actor || modelNode.actor === '') {
      const error: IValidationError = {
        message: `No Actor assigned for Step '${modelNode.text}'.`
      };
      errors.push(error);
      return;
    }
    const node = getNodeById(modelNode.id);
    if (
      node &&
      (node.StepType === ActivityTaskType.APPROVAL ||
        node.StepType === ActivityTaskType.TASK) &&
      (node.DynamicActorType === null || node.DynamicActorType === undefined) &&
      (node.AssignedUserId === null ||
        node.AssignedUserId === undefined ||
        node.AssignedUserId === EmptyGuid) &&
      (node.AssignedGroupId === null ||
        node.AssignedGroupId === undefined ||
        node.AssignedGroupId === EmptyGuid)
    ) {
      const error: IValidationError = {
        message: `'${modelNode.text}' must have an assigned actor.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }
  }

  function validateThatEndNodeOnlyHasToLinks(links) {
    if (links.endConnectors.length > 1) {
      const error: IValidationError = {
        message: 'Connectors can only flow to an END node.'
      };
      errors.push(error);
    }
  }

  function validateDecisionField(
    nodes: IModelerNode[],
    links: IModelerNodeLink[],
    gatewayNode: IModelerNode
  ) {
    let decisionFieldStep = nodes.find(
      (n) => n.key === gatewayNode.selectedNodeKey
    );
    if (decisionFieldStep === undefined) {
      let incomingLinks = flowinglyModelUtilityService.getLinksToNode(
        gatewayNode.key,
        links
      );
      let incomingNodes = flowinglyModelUtilityService.findNodesAtStartOfLinks(
        incomingLinks,
        nodes
      );
      while (
        incomingNodes &&
        incomingNodes.length > 0 &&
        incomingNodes[0].category !== 'activity'
      ) {
        incomingLinks = flowinglyModelUtilityService.getLinksToNode(
          incomingNodes[0].key,
          links
        );
        incomingNodes = flowinglyModelUtilityService.findNodesAtStartOfLinks(
          incomingLinks,
          nodes
        );
      }
      if (incomingNodes && incomingNodes.length > 0) {
        decisionFieldStep = nodes.find((n) => n.key === incomingNodes[0].key);
      }
    }

    if (decisionFieldStep === undefined) {
      const error: IValidationError = {
        message: `'${gatewayNode.text}' has an invalid Decision Step.`
      };
      modelerValidationErrorsService.addError(gatewayNode.key, error);
      errors.push(error);
      return;
    }

    const fieldsValidForDecision = getDecisionFieldsOfNodes([
      decisionFieldStep
    ]);
    const decisionField = fieldsValidForDecision.find((f) => {
      if (!gatewayNode.gateway.fieldId) {
        return;
      }
      const gatewayFieldIds = gatewayNode.gateway.fieldId.split('__');
      const gatewayFieldId = gatewayFieldIds[0];
      return f.name === gatewayFieldId;
    });

    if (!decisionField) {
      const error: IValidationError = {
        message: `'${gatewayNode.text}' has no Decision Field selected.`
      };
      modelerValidationErrorsService.addError(gatewayNode.key, error);
      errors.push(error);
      return;
    }

    const fieldDbName =
      decisionField.dbDataSource?.dbName ||
      decisionField.lookupConfig?.dbName ||
      '';
    const fieldDbDisplayValue =
      decisionField.dbDataSource?.displayValue ||
      decisionField.lookupConfig?.displayValue ||
      '';
    if (
      fieldDbName !== gatewayNode.gateway.dbName ||
      (fieldDbName !== '' &&
        fieldDbDisplayValue !== gatewayNode.gateway.dbFieldName)
    ) {
      const error: IValidationError = {
        message:
          `The Decision Field targeted by '${gatewayNode.text}' has changed, ` +
          `and '${gatewayNode.text}' needs to be reconfigured.`
      };
      modelerValidationErrorsService.addError(gatewayNode.key, error);
      errors.push(error);
      return;
    }

    const isMultiSelect =
      decisionField.type === flowinglyConstants.formFieldType.SELECT_LIST ||
      decisionField.type ===
        flowinglyConstants.formFieldType.RADIO_BUTTON_LIST ||
      decisionField.type === flowinglyConstants.formFieldType.CHECKBOX;

    if (isMultiSelect && gatewayNode.gateway.dbName === '') {
      if (decisionField.options.length < gatewayNode.gateway.gates.length) {
        // Less options than gateways
        const error: IValidationError = {
          message: `'${gatewayNode.text}' has too many paths. Add options to the previous task or remove paths.`
        };
        modelerValidationErrorsService.addError(gatewayNode.key, error);
        errors.push(error);
      }

      gatewayNode.gateway.gates.forEach((gate) => {
        if (
          isGateRouteAbsentInLinkDataArray(
            gatewayNode.key,
            gate.RouteToKey,
            links
          )
        ) {
          const error: IValidationError = {
            message: `${gatewayNode.text}'s connectors have changed and it needs to be reconfigured.`
          };
          modelerValidationErrorsService.addError(gatewayNode.key, error);
          errors.push(error);
          return;
        }

        if (gate.IsDefault) {
          return;
        }
        if (
          !gate.condition ||
          (gate.condition.Name === '' &&
            gate.condition.Value === guidService.empty()) ||
          (decisionField.type !== 'checkbox' &&
            (!gate.multiCondition || gate.multiCondition.length === 0))
        ) {
          const error: IValidationError = {
            message: `'${gatewayNode.text}' has an invalid Decision Path.`
          };
          modelerValidationErrorsService.addError(gatewayNode.key, error);
          errors.push(error);
        } else if (!gate.multiCondition || gate.multiCondition.length <= 1) {
          const option = decisionField.options.find(
            (option) =>
              gate.condition.Value === option.value ||
              gate.condition.Name === option.text
          );
          if (!option) {
            // Option was not located
            if (gate.condition.Name == null || gate.condition.Name === '') {
              const error: IValidationError = {
                message: `'${gatewayNode.text}' has a Decision Path with no value selected.`
              };
              modelerValidationErrorsService.addError(gatewayNode.key, error);
              errors.push(error);
            } else {
              // Check it is not a valid database dropdown list
              if (gate.condition.Name !== gate.condition.Value) {
                const error: IValidationError = {
                  message: `'${gatewayNode.text}' has an invalid Decision Path option: '${gate.condition.Name}'.`
                };
                modelerValidationErrorsService.addError(gatewayNode.key, error);
                errors.push(error);
              }
            }
          }
        }
      });
    }
  }

  function isGateRouteAbsentInLinkDataArray(
    gatewayNodeKey: number,
    gateRouteToKey: number,
    links: IModelerNodeLink[]
  ) {
    return !links.some(
      (link) => link.from === gatewayNodeKey && link.to === gateRouteToKey
    );
  }

  function validateGateWayConditions(modelNode, links) {
    const gatewayName = modelNode.text;
    let gotError = false;

    lodash.forEach(links, function (link) {
      if (
        link &&
        link.Trigger &&
        link.Trigger.NameRef &&
        link.Trigger.NameRef === 'GatewayDecisionCommand' &&
        !link.Conditions &&
        !link.isBacklink
      ) {
        gotError = true;
      }
    });

    if (gotError) {
      const error: IValidationError = {
        message: `'${gatewayName}' gateway contains invalid conditions.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }
  }

  function validateDynamicActorAfterInitialEmail(
    modelNode: IModelerNode,
    nodes: IModelerNode[],
    links: IModelerNodeLink[]
  ) {
    const nodesToCheck = [modelNode];
    while (nodesToCheck.length > 0) {
      const current = nodesToCheck.splice(0, 1)[0];
      const nextNodes = flowinglyModelUtilityService.getNextNodesForNode(
        current.key,
        nodes,
        links
      );
      for (const n of nextNodes) {
        if (
          n.category === NodeCategory.ACTIVITY &&
          n.actorType === ActorTypes.DYNAMIC &&
          n.actor === DynamicActorTypeNames.PREVIOUS_ACTOR
        ) {
          const error: IValidationError = {
            message: `'${n.text}' cannot assign to "Previous Actor" as there is no prior Step.`
          };
          modelerValidationErrorsService.addError(modelNode.key, error);
          errors.push(error);
          nodesToCheck.length = 0;
          break;
        }

        if (
          (n.category !== NodeCategory.ACTIVITY ||
            n.taskType === ActivityTaskType.CUSTOM_EMAIL) &&
          n.category !== NodeCategory.COMPONENT
        ) {
          nodesToCheck.push(n);
        }
      }
    }
  }

  function validateDynamicActor(
    modelNode: IModelerNode,
    nodes: IModelerNode[],
    links: IModelerNodeLink[]
  ) {
    const node = getNodeById(modelNode.id);
    if (
      node &&
      !isComponent &&
      node.IsFirstNode &&
      modelNode.actorType === ActorTypes.DYNAMIC &&
      modelNode.actor === DynamicActorTypeNames.PREVIOUS_ACTOR
    ) {
      const error: IValidationError = {
        message: `'${modelNode.text}' cannot assign to "Previous Actor" as it is the first Step.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }

    if (
      node &&
      modelNode.actorType === ActorTypes.DYNAMIC &&
      modelNode.actor === DynamicActorTypeNames.SELECT_DYNAMIC_ACTORS
    ) {
      const previousNodes =
        flowinglyModelUtilityService.getPreviousAcitivityNodesForNodeRecursive(
          modelNode.key,
          nodes,
          links,
          null,
          false
        );
      if (
        previousNodes.length > 0 &&
        previousNodes.some((n) => n.taskType === ActivityTaskType.CUSTOM_EMAIL)
      ) {
        const error: IValidationError = {
          message: `Dynamic Actor step '${modelNode.text}' cannot follow a Custom Email.`
        };
        modelerValidationErrorsService.addError(modelNode.key, error);
        errors.push(error);
      } else if (
        !node.SelectedDynamicActors ||
        node.SelectedDynamicActors.length === 0
      ) {
        const error: IValidationError = {
          message: `'${modelNode.text}' needs dynamic Actors to be selected.`
        };
        modelerValidationErrorsService.addError(modelNode.key, error);
        errors.push(error);
      }
    }

    if (
      firstStepIsNonIntraPublicNode() &&
      (modelNode.actor === DynamicActorTypeNames.INITIATOR ||
        modelNode.actor === DynamicActorTypeNames.INITIATOR_MANAGER)
    ) {
      const error: IValidationError = {
        message:
          `'${modelNode.text}' cannot be assigned to "Initiator" or "Initiator Manager" ` +
          'when the first step is a Public-type Public Form or External User Task.'
      };
      errors.push(error);
      modelerValidationErrorsService.addError(modelNode.key, error);
    }

    if (
      node &&
      modelNode.actorType === ActorTypes.DYNAMIC &&
      modelNode.actor === DynamicActorTypeNames.PREVIOUS_ACTOR
    ) {
      const previousNodes =
        flowinglyModelUtilityService.getPreviousAcitivityNodesForNodeRecursive(
          modelNode.key,
          nodes,
          links,
          null,
          false
        );

      if (
        previousNodes.length > 0 &&
        previousNodes.some(
          (n) => n.taskType === ActivityTaskType.EXTERNAL_USER_TASK
        )
      ) {
        const error: IValidationError = {
          message: `Step '${modelNode.text}' cannot use Step Assignee 'Previous Actor' when the previous step is an External User Task.`
        };
        modelerValidationErrorsService.addError(modelNode.key, error);
        errors.push(error);
      }
    }
  }

  function validateUniqueNodeId(modelNodes) {
    lodash.forEach(modelNodes, function (modelNode) {
      const items = lodash.filter(modelNodes, function (n) {
        return n.id === modelNode.id;
      });

      if (items.length > 1) {
        lodash.forEach(items, function (n) {
          const error: IValidationError = {
            message: `'${n.text}' has duplicate ID.`
          };
          modelerValidationErrorsService.addError(modelNode.key, error);
          errors.push(error);
        });
      }
    });
  }

  function validationSettingsAreValidForIPaaSIntegration(
    validationSettings: IValidationSettings
  ): boolean {
    return (
      validationSettings &&
      validationSettings.stepTypeNumberSupportedForIPaaS?.length > 0 &&
      validationSettings.validTaskTypesForIpaasIntegration?.length > 0
    );
  }

  function validateNodesForIpaasIntegration(
    modelNodes,
    validationSettings: IValidationSettings
  ) {
    // If IPassIntegration is disabled, IPassIntegration validations are skipped.
    if (!appConfig.enableIPaaSIntegration) {
      return;
    }

    if (!validationSettingsAreValidForIPaaSIntegration(validationSettings)) {
      return;
    }

    lodash.forEach(modelNodes, function (modelNode) {
      if (
        modelNode.iPaasIntegrationEnabled &&
        modelNode.taskType != null &&
        validationSettings.validTaskTypesForIpaasIntegration.includes(
          modelNode.taskType
        )
      ) {
        if (
          !validationSettings.stepTypeNumberSupportedForIPaaS.includes(
            modelNode.taskType
          )
        ) {
          const error: IValidationError = {
            message: `'${modelNode.text}' type is not supported for iPaaS integration. Disable iPaaS integration 
          for this step.`
          };
          modelerValidationErrorsService.addError(modelNode.key, error);
          errors.push(error);
        }
      }
    });
  }

  function validateMultipleApproval(modelNode) {
    const node = getNodeById(modelNode.id);

    if (node && node.IsFirstNode) {
      const error: IValidationError = {
        message: `The multiple approval Step '${node.StepName}' cannot be used as the first Step of a Flow Model.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
      return;
    }

    if (!node.SelectedApprovers || node.SelectedApprovers.length === 0) {
      const error: IValidationError = {
        message: `'${modelNode.text}' needs approvers to be selected.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }
  }

  function validateAssignedActorAndApproverForNodesAfterPublicForm(
    publicFormModelNode,
    modelNodes
  ) {
    const nodes = getNodes();
    for (const modelNode of modelNodes) {
      const matchNode = nodes.find((n) => {
        return n.ModelerNodeId === modelNode.id;
      });

      if (matchNode == null || (matchNode as any).isDeleted) {
        return;
      }

      // for both public and intranet forms
      if (modelNode.actor === DynamicActorTypeNames.SELECT_DYNAMIC_ACTORS) {
        const error: IValidationError = {
          message: `'${modelNode.text}' cannot be assigned to select dynamic actors when the first Step is a Public Form.`
        };
        modelerValidationErrorsService.addError(modelNode, error);
        errors.push(error);
      } else if (
        flowinglyModelUtilityService.isMultipleApprovalNode(modelNode)
      ) {
        const error: IValidationError = {
          message: `'${modelNode.text}' cannot be parallel/sequential approval when the first Step is a Public Form.`
        };
        modelerValidationErrorsService.addError(modelNode, error);
        errors.push(error);
      }
    }
  }

  function validateBacklinks(
    nodes: IModelerNode[],
    links: IModelerNodeLink[],
    nodesByKey: Map<number, IModelerNode>
  ) {
    const backlinks = links.filter((l) => l.isBacklink);
    backlinks.forEach((backlink) => {
      const fromNode = nodesByKey.get(backlink.from);
      const toNode = nodesByKey.get(backlink.to);

      if (validationService.isValidBackLink(fromNode, toNode, nodes, links)) {
        return;
      }

      // Generate appropriate error message based on target node type
      let message: string;
      if (toNode.taskType === ActivityTaskType.PUBLIC_FORM) {
        message = `Backlink from '${fromNode.text}' to '${toNode.text}' is invalid because '${toNode.text}' is a Public Form. Public Forms can only be submitted once per Flow`;

        if (appConfig.enableExternalUserStep) {
          message +=
            ', consider using an External User Task as the backlink target instead';
        }

        message += '.';
      } else {
        message = `Backlink from '${fromNode.text}' to '${toNode.text}' is invalid.`;
      }

      const error = {
        message: message
      };
      modelerValidationErrorsService.addError(fromNode.key, error);
      errors.push(error);
    });
  }

  function validateNodesReachableFromStart(
    nodes: IModelerNode[],
    links: IModelerNodeLink[]
  ): void {
    const unreachableNodeKeys = new Set(getUnreachableNodeKeys(nodes, links));

    nodes
      .filter((node) => unreachableNodeKeys.has(node.key))
      .forEach((node) => {
        const error: IValidationError = {
          message: `'${node.text}' is disconnected from the START node.`
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      });
  }

  function validateLoops(
    modelerNodes: IModelerNode[],
    nodes: IStepNode[],
    modelerNodesByKey: Map<number, IModelerNode>,
    linksByFromKey: Map<number, IModelerNodeLink[]>
  ) {
    for (const error of detectLoops(
      modelerNodes,
      nodes,
      modelerNodesByKey,
      linksByFromKey
    )) {
      modelerValidationErrorsService.addError(error.fromNodeKey, error);
      errors.push(error);
    }
  }

  function validateSelectApproversNode(workflowNodes, nodes, links) {
    workflowNodes.forEach((workflowNode) => {
      const goNodeData = nodes.find((n) => n.id == workflowNode.ModelerNodeId);

      if (
        !!workflowNode.isDeleted ||
        workflowNode.WhenApproversSelected ===
          flowinglyConstants.whenApproversSelected.MODEL_TIME ||
        (workflowNode.StepType !== ActivityTaskType.PARALLEL_APPROVAL &&
          workflowNode.StepType !== ActivityTaskType.SEQUENTIAL_APPROVAL)
      )
        return;

      const modelNode = flowinglyModelUtilityService.getNodeById(
        nodes,
        workflowNode.ModelerNodeId
      );
      if (!workflowNode.SelectApproverModelerNodeId) {
        const previousNodes =
          flowinglyModelUtilityService.getPreviousNodesForNode(
            modelNode.key,
            nodes,
            links
          );
        if (previousNodes.length === 1) {
          if (
            previousNodes[0].category ===
            flowinglyConstants.nodeCategory.CONVERGE_GATEWAY
          ) {
            const error: IValidationError = {
              message: `'${modelNode.text}' following a merge gateway requires you to select a Step where approvers will be chosen.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          } else if (
            previousNodes[0].taskType === ActivityTaskType.CUSTOM_EMAIL
          ) {
            const error: IValidationError = {
              message: `'${modelNode.text}' following a Custom Email requires you to select a Step where approvers will be chosen.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          } else if (previousNodes[0].taskType === ActivityTaskType.COMPONENT) {
            const error: IValidationError = {
              message: `'${modelNode.text}' following a Component requires you to select a Step where approvers will be chosen.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          } else if (
            previousNodes[0].taskType === ActivityTaskType.PARALLEL_APPROVAL ||
            previousNodes[0].taskType === ActivityTaskType.SEQUENTIAL_APPROVAL
          ) {
            const error: IValidationError = {
              message: `'${modelNode.text}' following a multiple approval Step requires you to select a Step where approvers will be chosen.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          } else if (
            previousNodes[0].taskType === ActivityTaskType.EXTERNAL_USER_TASK
          ) {
            const error: IValidationError = {
              message: `'${modelNode.text}' following a External User Task requires you to select a Step where approvers will be chosen.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          }
        }
        return;
      }

      const allPreviousNodes =
        flowinglyModelUtilityService.getPreviousAcitivityNodesForNodeRecursive(
          modelNode.key,
          nodes,
          links,
          null,
          true
        );
      const selectApproverNode = allPreviousNodes.find(
        (pn) => pn.id === workflowNode.SelectApproverModelerNodeId
      );
      if (
        !selectApproverNode ||
        !flowinglyActivityService.isTaskOrSingleApprovalActivity(
          selectApproverNode
        )
      ) {
        const error: IValidationError = {
          message: `'${modelNode.text}' requires you to select valid Step where approvers will be chosen.`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
      }
    });
  }

  function validateCustomEmailNode(
    workflowNodes,
    nodes: IModelerNode[],
    links
  ) {
    workflowNodes.forEach((workflowNode) => {
      const goNodeData = nodes.find((n) => n.id == workflowNode.ModelerNodeId);
      const isCustomEmail =
        workflowNode.StepType === ActivityTaskType.CUSTOM_EMAIL;
      const isExternalUserTask =
        workflowNode.StepType === ActivityTaskType.EXTERNAL_USER_TASK;
      if (!!workflowNode.isDeleted || (!isCustomEmail && !isExternalUserTask))
        return;

      if (
        isCustomEmail &&
        workflowNode.IsFirstNode &&
        appConfig.workflowParserVersion === 1
      ) {
        const error: IValidationError = {
          message: `The Custom Email '${workflowNode.StepName}' cannot be used as the first Step of a Flow Model.`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
        return;
      }

      if (!workflowNode.NodeCustomEmail) {
        const error: IValidationError = {
          message: `'${workflowNode.StepName}' requires you to configure Email data.`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
        return;
      }

      if (!workflowNode.NodeCustomEmail.FromName) {
        const error: IValidationError = {
          message: `'${workflowNode.StepName}' requires you to configure Email "From Name".`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
      }
      if (!workflowNode.NodeCustomEmail.ReplyTo) {
        const error: IValidationError = {
          message: `'${workflowNode.StepName}' requires you to configure Email "Reply To".`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
      }

      const template = workflowNode.NodeCustomEmail.TemplateHtml;
      if (!template) {
        const error: IValidationError = {
          message: `'${workflowNode.StepName}' requires you to design the email contents.`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
      } else {
        const invalidVariables = getInvalidTemplateVariablesForPriorNodes(
          template,
          workflowNode,
          workflowNodes,
          nodes,
          links
        );
        if (invalidVariables) {
          invalidVariables.forEach((variable) => {
            const error: IValidationError = {
              message: `'${workflowNode.StepName}' contains invalid Step field variable '${variable}' in the email content.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          });
        }
      }

      const recipients = workflowNode.NodeCustomEmail.SelectedEmailRecipients;
      if (
        (isCustomEmail || isExternalUserTask) &&
        !workflowNode.NodeCustomEmail.AdditionalRecipients &&
        (!recipients ||
          recipients.length === 0 ||
          isAllEmailRecipientInvalidFields(
            recipients,
            workflowNode,
            workflowNodes,
            nodes,
            links
          ))
      ) {
        const error: IValidationError = {
          message: `'${workflowNode.StepName}' requires you to configure ${
            isExternalUserTask ? '"External User Email"' : '"Recipient List"'
          }.`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
      }

      if (
        isExternalUserTask &&
        workflowNode.NodeCustomEmail.AdditionalRecipients
      ) {
        const recipients = workflowNode.NodeCustomEmail.AdditionalRecipients;

        userApiService
          .isValidExternalUser(recipients)
          .then((response: ExternalUserValidationResult) => {
            if (!response.isValid && response.invalidEmails?.length) {
              const invalidEmailsList = response.invalidEmails.join(', ');
              const errorMessage = `'${workflowNode.StepName}' has invalid external user(s): ${invalidEmailsList}. Unlicensed business users cannot be added as external users.`;

              const error: IValidationError = { message: errorMessage };
              modelerValidationErrorsService.addError(goNodeData.key, error);
              errors.push(error);
            }
          });
      }

      if (!workflowNode.NodeCustomEmail.Subject) {
        const error: IValidationError = {
          message: `'${workflowNode.StepName}' requires you to configure "Subject".`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
      } else {
        const invalidVariables = getInvalidTemplateVariablesForPriorNodes(
          workflowNode.NodeCustomEmail.Subject,
          workflowNode,
          workflowNodes,
          nodes,
          links
        );
        if (invalidVariables) {
          invalidVariables.forEach((variable) => {
            const error: IValidationError = {
              message: `'${workflowNode.StepName}' contains invalid Step field variable '${variable}' in its email subject.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          });
        }
      }

      if (isExternalUserTask && !workflowNode.NodeCustomEmail.ActionLabel) {
        const error: IValidationError = {
          message: `'${workflowNode.StepName}' requires you to configure "Action Button Label".`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
      }
    });
  }

  function getInvalidTemplateVariablesForPriorNodes(
    template,
    workflowNode,
    workflowNodes,
    nodes,
    links
  ) {
    template = htmlDecode(template);
    const nonBreakingSpace = String.fromCharCode(160);
    template = template.replaceAll(nonBreakingSpace, ' ');
    const modelNode = flowinglyModelUtilityService.getNodeById(
      nodes,
      workflowNode.ModelerNodeId
    );
    let stepFields = [];
    const allPreviousNodes =
      flowinglyModelUtilityService.getPreviousAcitivityNodesForNodeRecursive(
        modelNode.key,
        nodes,
        links,
        null,
        true
      );
    allPreviousNodes.forEach((modelerNode) => {
      const node = workflowNodes.find(
        (wfn) => wfn.ModelerNodeId === modelerNode.id
      );
      const variables = getStepFieldVariables(node);
      stepFields = stepFields.concat(variables);
    });

    return checkTemplateForInvalidStepFields(template, stepFields);
  }

  function getInvalidTemplateVariablesForNode(template, node) {
    template = htmlDecode(template);
    const stepFields = getStepFieldVariables(node);
    return checkTemplateForInvalidStepFields(template, stepFields);
  }

  function checkTemplateForInvalidStepFields(template, stepFields) {
    stepFields.forEach((sf) => {
      const re = new RegExp(escapeRegExp(sf), 'g');
      template = template.replace(re, '');
    });
    const regexForInvalidStepField = /{step\..+?\..+?}/g;
    return template.match(regexForInvalidStepField);
  }

  function getStepFieldVariables(node) {
    if (!node || !node.Card || !node.Card.formElements) {
      return;
    }
    const stepFields = [];
    const formElements = node.Card.formElements;
    formElements.forEach((field) => {
      stepFields.push(`{step.${node.StepName}.${field.displayName}}`);
    });

    const tableFields = formElements.filter(
      (field) => field.typeName == 'Table'
    );
    if (tableFields && tableFields.length > 0) {
      const tableCells = flowinglyModelUtilityService.transformTableCellToField(
        flowinglyModelUtilityService.getEligibleCellsInFields(tableFields)
      );

      if (tableCells && tableCells.length > 0) {
        tableCells.forEach((cell) => {
          stepFields.push(`{step.${node.StepName}.${cell.displayName}}`);
        });
      }
    }

    // add extra step variables
    stepFields.push(`{step.${node.StepName}.Step Name}`);
    stepFields.push(`{step.${node.StepName}.User Who Approved}`);
    stepFields.push(`{step.${node.StepName}.Approval Date}`);
    stepFields.push(`{step.${node.StepName}.Comment Made By User}`);
    return stepFields;
  }

  function htmlDecode(value) {
    return $('<div/>').html(value).text(); // -- *shakes fist* damn ts
  }

  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  function isAllEmailRecipientInvalidFields(
    recipients,
    workflowNode,
    workflowNodes,
    nodes,
    links
  ) {
    return recipients.every((r) => {
      if (r.SearchEntityType !== 'StepFormField') return false;

      const modelNode = flowinglyModelUtilityService.getNodeById(
        nodes,
        workflowNode.ModelerNodeId
      );
      const allPreviousModelerNodes =
        flowinglyModelUtilityService.getPreviousAcitivityNodesForNodeRecursive(
          modelNode.key,
          nodes,
          links,
          null,
          true
        );
      const recipientModelerNode = allPreviousModelerNodes.find(
        (n) => n.id === r.FieldModelerNodeId
      );
      if (!recipientModelerNode) return true;

      const formElements = workflowNodes.find(
        (wfn) => wfn.ModelerNodeId === recipientModelerNode.id
      ).Card.formElements;
      const formField = formElements.find((formElement) => {
        return formElement.name === r.FieldName;
      });
      if (
        !formField ||
        (formField.type !== flowinglyConstants.formFieldType.EMAIL &&
          formField.type !== flowinglyConstants.formFieldType.LOOKUP &&
          formField.type !== flowinglyConstants.formFieldType.DROPDOWN_LIST)
      )
        return true;

      return false;
    });
  }

  function findLinksFrom(gatewayNode, links) {
    const connectedLinks = lodash.filter(links, function (link) {
      return link.from === gatewayNode.key;
    });
    return connectedLinks;
  }

  function findNodesFrom(nodes, links) {
    const connectedNodes = [];
    lodash.forEach(links, function (link) {
      const node = lodash.find(nodes, function (node) {
        return node.key === link.to;
      });
      connectedNodes.push(node);
    });
    return connectedNodes;
  }

  function getDecisionFieldsOfNodes(nodes: IModelerNode[]) {
    const fields: IFormField[] = [] as IFormField[];
    for (const modelNode of nodes || []) {
      const modelerNode = getNodeById(modelNode.id);
      if (
        typeof modelerNode.Card === 'string' ||
        !Array.isArray(modelerNode.Card.formElements)
      ) {
        continue;
      }
      for (const formField of modelerNode.Card.formElements) {
        if (isValidDecisionFieldType(formField.type)) {
          fields.push(formField);
        }
      }
    }
    return fields;
  }

  // This and other functionality used here was copy and pasted and improved from gateway.store.js! Yeah I know ...
  // ToDo we need to refactor design into this code so we have a single component to access such functionality
  // For example we might have a FieldType component. Right now in the code base we have four major issues:
  //  1. Functionality is all over the place and functions are dependant on state (ie arguments not used)
  //  2. We dont follow CQS - so for example a method called FindGates will set the first gate to being the default (ie it changes state)
  //  3. State is duplicated all over the place - so we need a stateful, organised model.  eg nodeDataArray is stored in many places
  //  4. Events and watches conspire to make tracking what happens when, very difficult
  function isValidDecisionFieldType(type) {
    switch (type) {
      case 'instruction':
      case 'text':
      case 'textarea':
      case 'email':
      case 'password':
      case 'fileupload':
      case 'approvalrule':
      case 'tasklist':
      case 'multiselectlist':
      case 'date':
      case '7': //for few cards having this type
      case 'attachdocument':
        return false;

      default:
        //checkbox
        //radioButtonList
        //selectList
        //number
        //currency
        return true;
    }
  }

  function validateDivergeGateWay(allModelNodes, allLinks, node, nodeLinks) {
    if (
      appConfig.workflowParserVersion === 1 &&
      nodeLinks.foreToConnectorCount > 1
    ) {
      const error: IValidationError = {
        message: `Diverge nodes can only have one incoming connector. Remove multiple incoming connectors.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
    if (nodeLinks.foreFromConnectorCount < 2) {
      const error: IValidationError = {
        message: `Diverge gateway only has one or fewer outgoing connectors. Connect at least two outgoing connectors."`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
      return;
    }

    const nextNodes = flowinglyModelUtilityService.getNextNodesForNode(
      node.key,
      allModelNodes,
      allLinks
    );
    const eventNode = nextNodes.find((n) => n.category === NodeCategory.EVENT);
    if (eventNode) {
      const error: IValidationError = {
        message: `Diverge cannot connect directly to ${eventNode.text}.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }

    const divergeRuleData = rulesetParserService.divergeParser(nextNodes);
    if (divergeRuleData && divergeRuleData.length > 0) {
      const validationResult = rulesetValidationService.validateRuleData(
        rulesetService.DIVERGE_RULESET,
        divergeRuleData
      );

      if (!validationResult.isValid) {
        const error: IValidationError = {
          message: `${validationResult.errorMessage} is not allowed for diverge.`
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
    }
  }

  //function to check a particular node Category is present in the previous nodes
  function checkCategoryInPrevNodes(
    fromLinks,
    nodeCategory,
    allLinks,
    allModelNodes
  ) {
    for (let i = 0; i < fromLinks.length; i++) {
      let invalidNode = false;
      let linkKey = fromLinks[i].from;
      while (!invalidNode) {
        const toLinks = lodash.filter(allLinks, function (link) {
          return link.to === linkKey && !link.isBacklink;
        });
        if (toLinks && toLinks.length > 0) {
          for (let tl = 0; tl < toLinks.length; tl++) {
            const prevNode = flowinglyModelUtilityService.getNodeByKey(
              allModelNodes,
              toLinks[tl].from
            );
            if (prevNode) {
              linkKey = prevNode.key;
              if (prevNode.category === nodeCategory) {
                return true;
              } else if (
                prevNode.IsInitial === 'true' ||
                prevNode.IsFinal === 'true'
              ) {
                invalidNode = true;
              }
            } else {
              invalidNode = true;
            }
          }
        } else {
          invalidNode = true;
        }
      }
    }
    return false;
  }
  function validateConvergeGateWay(
    allModelNodes: IModelerNode[],
    allLinks: IModelerNodeLink[],
    node: IModelerNode,
    nodeLinks
  ) {
    checkConvergeHasDiverge(allModelNodes, allLinks, node.key);
    checkConvergeNotLinkedFromFutureNode(
      allModelNodes,
      allLinks,
      node.key,
      node.key
    );

    if (
      (appConfig.workflowParserVersion === 1 &&
        nodeLinks.foreToConnectorCount < 2) ||
      nodeLinks.foreToConnectorCount < 1
    ) {
      const error: IValidationError = {
        message: `Merge gateway has too few incoming connectors. All paths from the Diverge must meet at the Merge unless ended."`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
      return;
    }

    if (appConfig.workflowParserVersion === 1) {
      const nodes = flowinglyModelUtilityService.getPreviousNodesForNode(
        node.key,
        allModelNodes,
        allLinks
      );

      const convergeRuleData = rulesetParserService.convergeParser(nodes);
      if (convergeRuleData && convergeRuleData.length > 0) {
        const validationResult = rulesetValidationService.validateRuleData(
          rulesetService.CONVERGE_RULESET,
          convergeRuleData
        );

        if (!validationResult.isValid) {
          const error: IValidationError = {
            message: `${validationResult.errorMessage} is not allowed.`
          };
          modelerValidationErrorsService.addError(node.key, error);
          errors.push(error);
        }
      }
    }
  }

  function checkConvergeHasDiverge(
    nodes: IModelerNode[],
    links: IModelerNodeLink[],
    convergeNodeKey: number
  ) {
    const priorNodes = flowinglyModelUtilityService.getPreviousNodesForNode(
      convergeNodeKey,
      nodes,
      links
    );
    const visitedNodes = [];
    while (priorNodes.length > 0) {
      const currentNode = priorNodes.pop();
      if (
        currentNode.category === flowinglyConstants.nodeCategory.DIVERGE_GATEWAY
      ) {
        return;
      }
      visitedNodes.push(currentNode);
      const currentNodePriors =
        flowinglyModelUtilityService.getPreviousNodesForNode(
          currentNode.key,
          nodes,
          links
        );
      const unvisitedNodes = currentNodePriors.filter(
        (prior) => !visitedNodes.some((visited) => visited.key === prior.key)
      );
      priorNodes.push(
        ...unvisitedNodes.filter((node) => node.key !== convergeNodeKey)
      );
    }

    const error: IValidationError = {
      message:
        'There is a Merge gateway with no matching Diverge, Merges are only used to merge paths coming from a Diverge.'
    };
    modelerValidationErrorsService.addError(convergeNodeKey, error);
    errors.push(error);
  }

  function checkConvergeNotLinkedFromFutureNode(
    nodes: IModelerNode[],
    links: IModelerNodeLink[],
    currentNodeKey: number,
    convergeNodeKey: number,
    nodeKeysFromConvergeToCurrent: Set<number> = new Set(),
    visitedNodeKeys: Set<number> = new Set()
  ) {
    const priorNodes = flowinglyModelUtilityService.getPreviousNodesForNode(
      currentNodeKey,
      nodes,
      links
    );
    if (priorNodes.length === 0) {
      return;
    }
    if (priorNodes.some((node) => node.key === convergeNodeKey)) {
      const error: IValidationError = {
        message:
          'There is a connector entering a Merge gateway that comes from a Node after the Merge,' +
          ' paths cannot be merged backwards.'
      };
      modelerValidationErrorsService.addError(convergeNodeKey, error);
      errors.push(error);
      return;
    }
    const revisitedNodes = priorNodes.filter((prior) =>
      nodeKeysFromConvergeToCurrent.has(prior.key)
    );
    for (const revisitedNode of revisitedNodes) {
      const currentNode = nodes.find((node) => node.key === currentNodeKey);
      const error: IValidationError = {
        message:
          `Invalid loop detected between '${revisitedNode.text}' and '${currentNode.text}',` +
          ' if the model looks correct try deleting then recreating the connector between these nodes.'
      };
      modelerValidationErrorsService.addError(currentNode.key, error);
      errors.push(error);
    }
    for (const priorNode of priorNodes) {
      if (!visitedNodeKeys.has(priorNode.key)) {
        visitedNodeKeys.add(priorNode.key);
        checkConvergeNotLinkedFromFutureNode(
          nodes,
          links,
          priorNode.key,
          convergeNodeKey,
          new Set([...nodeKeysFromConvergeToCurrent, priorNode.key]),
          visitedNodeKeys
        );
      }
    }
  }

  function validateExclusiveGateWay(modelNodes, node, links) {
    const validateNodeValues = function (gates) {
      for (let firstIndex = 0; firstIndex < gates.length; firstIndex++) {
        const gate = gates[firstIndex];
        const foundModelNodes = lodash.filter(modelNodes, function (modelNode) {
          return modelNode.key === gate.RouteToKey;
        });
        if (
          foundModelNodes === undefined ||
          foundModelNodes === null ||
          foundModelNodes.length < 1
        ) {
          const error: IValidationError = {
            message: `Gateway '${node.text}' has an invalid path to '${gate.name}'.`
          };
          modelerValidationErrorsService.addError(node.key, error);
          errors.push(error);
        }

        if (gate.IsDefault) continue;
      }

      /*
       * Check if any of the options are used in multiple gateway paths.
       */
      const duplicateValues = [];
      lodash
        .chain(node.gateway.gates)
        .flatMap((gate) => lodash.get(gate, 'condition.Name', '').split('|'))
        .groupBy()
        .map((grouping, key) => {
          if (grouping.length > 1 && key !== '') {
            duplicateValues.push(key);
          }
        })
        .value();
      if (duplicateValues.length) {
        const error: IValidationError = {
          message:
            `"${node.text}" has conditions which are the same ` +
            `"${duplicateValues.join(',')}". ` +
            'Select unique conditions for each path.'
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
    };

    if (links.foreFromConnectorCount < 2) {
      const error: IValidationError = {
        message: `'${node.text}' has one or fewer outgoing connectors. Connect at least two outgoing connectors.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }

    ////check that the node has a valid name
    if (node.text === '') {
      const error: IValidationError = {
        message: 'Decision must have a name.'
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }

    if (node.gateway) {
      if (node.gateway.fieldId === '' || node.gateway.fieldId == undefined) {
        const error: IValidationError = {
          message: `'${node.text}' must have a decision field. Select '${node.text}' and choose a decision field.`
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
      let conditionsMissing = false;
      if (node.gateway.gates) {
        lodash.forEach(node.gateway.gates, function (gate) {
          if (
            (gate.condition == undefined ||
              gate.condition.Value === '' ||
              gate.condition.Value == undefined) &&
            !gate.IsDefault
          ) {
            conditionsMissing = true;
          }
        });
        if (conditionsMissing) {
          const error: IValidationError = {
            message: `'${node.text}' is missing conditions. Select unique conditions for each path.`
          };
          modelerValidationErrorsService.addError(node.key, error);
          errors.push(error);
        }
        if (node.gateway.gates.length !== links.foreFromConnectorCount) {
          const error: IValidationError = {
            message: `'${node.text}' has new connector(s). Confirm new decision paths before publishing, or remove unnecessary connector(s).`
          };
          modelerValidationErrorsService.addError(node.key, error);
          errors.push(error);
        }
        validateNodeValues(node.gateway.gates);
      } else {
        const error: IValidationError = {
          message: `'${node.text}' is missing conditions. Select unique conditions for each path.`
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
    }
  }

  function getAllModelNodes(model) {
    return model.nodeDataArray;
  }

  function getAllModelLinks(model) {
    return model.linkDataArray;
  }

  /**
   * Returns all the workflow nodes (the none gojs one).
   *
   * This function is depracted because it s reliant on a global function.
   * Consider using context either
   *      context.queryable.nodes.toList()
   *      workflow.Nodes
   * @deprecated
   */
  function getNodes() {
    console.debug(new Error('@deprecated function getNodes() used'));
    return workflow.Nodes || [];
  }

  function getNodeById(modelerNodeId) {
    console.debug(new Error('@deprecated function getNodeById() used'));
    const allWorkflowNodes = getNodes();
    const node = allWorkflowNodes.find((n) => {
      return n.ModelerNodeId.toLowerCase() === modelerNodeId.toLowerCase();
    });

    return node;
  }

  function getLinks(model, node) {
    const res = {
      startConnectors: [],
      endConnectors: [],
      foreToConnectorCount: 0,
      foreFromConnectorCount: 0,
      backToConnectorCount: 0,
      backFromConnectorCount: 0,
      hasToConnector: false
    } as any;
    const arr = model.linkDataArray;
    lodash.forEach(arr, function (link) {
      if (node.IsInitial === 'true' && node.key === link.to) {
        res.startConnectors.push(node);
      } else if (node.IsFinal === 'true' && node.key === link.from) {
        res.endConnectors.push(node);
      } else if (node.key === link.to) {
        res.hasToConnector = true;
        link.isBacklink
          ? res.backToConnectorCount++
          : res.foreToConnectorCount++;
      } else if (node.key === link.from) {
        res.hasFromConnector = true;
        link.isBacklink
          ? res.backFromConnectorCount++
          : res.foreFromConnectorCount++;
      }
    });
    return res;
  }

  function validateEndObject(allModelNodes) {
    const endNodes = lodash.filter(allModelNodes, function (item) {
      return item.IsFinal === 'true';
    });
    if (endNodes === undefined || endNodes === null || endNodes.length < 1) {
      const error: IValidationError = {
        message: 'There must be at least one END node.'
      };
      errors.push(error);
    }
  }

  function validateStartObject(allModelNodes) {
    const startNodes = lodash.filter(allModelNodes, function (item) {
      return item.IsInitial === 'true';
    });
    if (
      startNodes === undefined ||
      startNodes === null ||
      startNodes.length < 1
    ) {
      const error: IValidationError = {
        message: 'There must be a START node.'
      };
      errors.push(error);
    } else if (startNodes.length > 1) {
      const error: IValidationError = {
        message: 'Only one START node is allowed.'
      };
      errors.push(error);
    }
  }

  function validateThatStartNodeOnlyHasFromLinks(links) {
    if (links.startConnectors.length > 1) {
      const error: IValidationError = {
        message: 'Connectors can only flow from a START node.'
      };
      errors.push(error);
    }
  }

  function validateThatNodeDoesNotHaveMultipleFromLinks(links, node) {
    if (flowinglyActivityService.isApprovalActivity(node)) {
      approvalNodeDoesNotHaveMultipleBackOrForeFromConnectors(links, node);
      approvalNodeHasForelinkWhenBackFromConnectorExists(links, node);
    } else if (
      (!flowinglyGatewayService.isGateway(node) &&
        links.foreFromConnectorCount > 1) ||
      (flowinglyGatewayService.isConvergeGateway(node) &&
        links.foreFromConnectorCount > 1)
    ) {
      const error: IValidationError = {
        message: `Step '${node.text}' cannot have multiple FROM connectors.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
  }

  function approvalNodeDoesNotHaveMultipleBackOrForeFromConnectors(
    links,
    node
  ) {
    if (links.foreFromConnectorCount > 1) {
      const error: IValidationError = {
        message: `Step '${node.text}' cannot have multiple outgoing forward connectors.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
    if (links.backFromConnectorCount > 1) {
      const error: IValidationError = {
        message: `Step '${node.text}' cannot have multiple outgoing back connectors.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
  }

  function approvalNodeHasForelinkWhenBackFromConnectorExists(links, node) {
    if (
      links.backFromConnectorCount > 0 &&
      links.foreFromConnectorCount === 0
    ) {
      const error: IValidationError = {
        message: `Step '${node.text}' cannot have outgoing back connector without outgoing forward connector.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
  }

  function validateNodeHasFromConnector(links, node) {
    if (
      !links.hasFromConnector &&
      node.IsFinal !== 'true' &&
      node.category !== 'Pool' &&
      node.category !== 'Lane'
    ) {
      const error: IValidationError = {
        message: `No FROM connector exists for Step '${node.text}'.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
  }

  function validateNodeHasToConnector(links, node) {
    if (
      !links.hasToConnector &&
      node.IsInitial !== 'true' &&
      node.category !== 'Pool' &&
      node.category !== 'Lane'
    ) {
      const error: IValidationError = {
        message: `No TO connector exists for Step '${node.text}'.`
      };
      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
  }

  function validateAtleastOneStep(allModelNodes) {
    if (
      !allModelNodes.some(
        (node) =>
          (node.category === flowinglyConstants.nodeCategory.ACTIVITY ||
            node.category === flowinglyConstants.nodeCategory.COMPONENT) &&
          node.taskType !== ActivityTaskType.CUSTOM_EMAIL
      )
    ) {
      const error: IValidationError = {
        message: 'Flow Model requires at least one Step'
      };
      errors.push(error);
    }
  }

  function validateNodeFields(
    firstNode: IModelerNode | null,
    modelNode: IModelerNode,
    allCustomDbs
  ) {
    if (
      modelNode.category !== 'activity' ||
      modelNode.taskType === ActivityTaskType.CUSTOM_EMAIL
    ) {
      return;
    }
    const card = getNodeById(modelNode.id).Card as IRunnerCard;
    const nonLogoFieldCount = card.formElements.filter(
      (field) => field.type !== flowinglyConstants.formFieldType.IMAGE
    ).length;

    const defaultInstructionFieldExists = card.formElements.some(
      (field) =>
        field.type === flowinglyConstants.formFieldType.INSTRUCTION &&
        field.value === 'Enter the instructions to be displayed here.'
    );

    const logoField = card.formElements.find(
      (field) => field.type === flowinglyConstants.formFieldType.IMAGE
    );

    if (nonLogoFieldCount === 0) {
      const error: IValidationError = {
        message:
          `'${modelNode.text}' needs to have at least one field on the card` +
          (logoField ? ' in addition to the Logo.' : '.')
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    } else if (
      nonLogoFieldCount === 1 &&
      defaultInstructionFieldExists &&
      (!logoField || logoField.disabled)
    ) {
      // Although this warning talks about the instruction field
      // Really, it's a reminder to the user that they haven't made any changes to this step's form
      const error: IValidationError = {
        message: `'${modelNode.text}' requires you to edit the default instruction text.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }

    const recaptchaFields = card.formElements.filter(
      (field) => field.type === FormFieldType.RECAPTCHA
    );

    if (recaptchaFields.length > 1) {
      const error: IValidationError = {
        message: `'${modelNode.text}' should have only one recaptcha field`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }

    card.formElements
      .filter(
        (field) =>
          field.type === FormFieldType.INSTRUCTION &&
          !field.displayName &&
          field.showDisplayName
      )
      .forEach((field) => {
        const error = {
          message: `'${modelNode.text}' requires you to add display name for instruction field`
        };
        modelerValidationErrorsService.addError(modelNode.key, error);
        errors.push(error);
      });

    validateOptions(
      modelNode,
      card.formElements,
      'tasklist',
      'Task',
      allCustomDbs
    );
    validateOptions(
      modelNode,
      card.formElements,
      'multiselectlist',
      'Multi-selection',
      allCustomDbs
    );
    validateOptions(
      modelNode,
      card.formElements,
      'radiobuttonlist',
      'Option',
      allCustomDbs
    );
    validateOptions(
      modelNode,
      card.formElements,
      'selectlist',
      'DropDown',
      allCustomDbs
    );
    validateTable(firstNode, modelNode, card.formElements, allCustomDbs);
    validateProcedures(modelNode, card.formElements);

    if (modelNode.taskType === ActivityTaskType.PUBLIC_FORM) {
      validatePublicFormSettings(modelNode, errors);
    }
  }

  /**
   * This is a matrix of the expected results
   *
   *      publicFormType  | PublicFormType    | result
   *            0         |        1          | 0
   *            1         |        0          | 1
   *           "1"        |        0          | 1
   *           "0"        |        1          | 0
   *        <null|undef>  |       "1"         | 1
   *        <null|undef>  |        1          | 1
   *        <null|undef>  |       "0"         | 0
   *        <null|undef>  |   <null|undef>    | 0
   */
  function getPublicFormType(someNodeWhoseTypeImUnsureOf) {
    const tmp = someNodeWhoseTypeImUnsureOf;
    const var1 = tmp.publicFormType;
    const var2 = tmp.PublicFormType;

    const v = +coalesce(var1, var2);

    return isNaN(v) ? 0 : v;
  }

  function coalesce(...args) {
    for (const arg of args) {
      if (arg !== null && arg !== undefined && !isNaN(arg)) {
        return arg;
      }
    }
  }

  function firstStepIsAnyPublicNode(firstStep: IStepNode) {
    const p1 = (firstStep as any).publicFormType;
    const p2 = firstStep.PublicFormType;
    const hasPublicType = p1 != null || p2 != null;
    return (
      firstStep.StepType === ActivityTaskType.EXTERNAL_USER_TASK ||
      firstStep.StepType === ActivityTaskType.PUBLIC_FORM ||
      (hasPublicType && firstStep.ActorName == 'Public User')
    );
  }

  /**
   * This is a frigging joke. Why are there two cases of publicFormType? Sometimes you get it in camelcase
   * sometimes you get it in pascal case. Depends on when the publicFormType is set.
   *
   * @TODO standardize this! ONLY HAVE ONE CASE
   *                                                  -Cassey
   */
  function firstStepIsNonIntraPublicNode() {
    const firstStep = getNodes().find((n) => n.IsFirstNode);
    if (!firstStep) {
      return false;
    }
    const publicFormType = getPublicFormType(firstStep);
    return (
      publicFormType !== PublicFormType.Intranet &&
      firstStepIsAnyPublicNode(firstStep)
    );
  }

  /**
   * How to trigger:
   *
   *      Create a field with a field that gets its value from a previous step
   *      Delete the referenced field
   *
   * @param param0
   */
  function validatePreviousField(goNodeData, node, allWorkflowNodes, field) {
    const hasPreviousField =
      field.defaultValueOption &&
      field.defaultPreviousStepId &&
      field.defaultFormFieldId &&
      field.defaultValueOption === 'previousValue';

    if (!hasPreviousField) {
      return;
    }

    const previousStep = allWorkflowNodes.find(
      (n) => n.ModelerNodeId === field.defaultPreviousStepId
    );

    if (!previousStep) {
      const error: IValidationError = {
        message: `The default field setup for Step '${node.StepName}' field '${field.displayName}' is missing.`
      };
      modelerValidationErrorsService.addError(goNodeData.key, error);
      errors.push(error);
      return;
    } else {
      let allFields = angular.copy(previousStep.Card.formElements);
      const tableFields = allFields.filter((f) => f.typeName === 'Table');
      if (tableFields && tableFields.length > 0) {
        const tableCells =
          flowinglyModelUtilityService.transformTableCellToField(
            flowinglyModelUtilityService.getEligibleCellsInFields(tableFields)
          );
        allFields = allFields.concat(tableCells);
      }
      const defaultFormField = allFields.find(
        (pf) => pf.name === field.defaultFormFieldId
      );

      if (!defaultFormField) {
        const error: IValidationError = {
          message: `The default field setup for Step '${node.StepName}' field '${field.displayName}' is missing.`
        };
        modelerValidationErrorsService.addError(goNodeData.key, error);
        errors.push(error);
        return;
      }
    }
  }

  function validateFieldConditions(context) {
    const allWorkflowNodes = context.workflowNodes;

    allWorkflowNodes.forEach((node) => {
      const fieldsWithConditions = node.Card.formElements.filter(
        (f) => f.conditions && f.conditions.length > 0
      );
      const goNodeData = context.queryable.goNodeData.getByIndex(
        'id',
        node.ModelerNodeId
      );

      fieldsWithConditions.forEach((field) => {
        field.conditions.forEach((fieldCondition) => {
          if (!fieldCondition.name) {
            const error: IValidationError = {
              message: `Field Condition for field '${field.displayName}' in '${node.StepName}' must have a name for condition.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          }

          if (
            fieldCondition.decisions == null ||
            fieldCondition.decisions.length === 0
          ) {
            const error: IValidationError = {
              message: `Field Condition '${fieldCondition.name}' for field '${field.displayName}' in '${node.StepName}' must have decisions set.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          }

          if (
            fieldCondition.actions == null ||
            fieldCondition.actions.length === 0
          ) {
            const error: IValidationError = {
              message: `Field Condition '${fieldCondition.name}' for field '${field.displayName}' in '${node.StepName}' must have actions set.`
            };
            modelerValidationErrorsService.addError(goNodeData.key, error);
            errors.push(error);
          }

          fieldCondition.decisions.forEach((decision) => {
            const formFieldConditionOption =
              flowinglyConstants.formFieldConditionOption;
            const condition = getEnumValue(
              formFieldConditionOption,
              decision.condition
            ).toString();

            if (!decision.decisionField) {
              const error: IValidationError = {
                message: `Field Condition '${fieldCondition.name}' for field '${field.displayName}' in '${node.StepName}' must have a decision field.`
              };
              modelerValidationErrorsService.addError(goNodeData.key, error);
              errors.push(error);
            }

            if (condition == '-1') {
              const error: IValidationError = {
                message: `Field Condition '${fieldCondition.name}' for field '${field.displayName}' in '${node.StepName}' must have a condition.`
              };
              modelerValidationErrorsService.addError(goNodeData.key, error);
              errors.push(error);
            }

            if (
              (condition == formFieldConditionOption.Equals.toString() ||
                condition == formFieldConditionOption.NotEquals.toString()) &&
              decision.value.toString() == '-1'
            ) {
              const error: IValidationError = {
                message: `Field Condition '${fieldCondition.name}' for field '${field.displayName}' in '${node.StepName}' must have a value.`
              };
              modelerValidationErrorsService.addError(goNodeData.key, error);
              errors.push(error);
            }
          });

          fieldCondition.actions.forEach((action) => {
            if (action.actionField.toString() == '-1') {
              const error: IValidationError = {
                message: `Field Condition '${fieldCondition.name}' for field '${field.displayName}' in '${node.StepName}' must have an action field.`
              };
              modelerValidationErrorsService.addError(goNodeData.key, error);
              errors.push(error);
            }

            if (action.action == null || action.action.toString() == '-1') {
              const error: IValidationError = {
                message: `Field Condition '${fieldCondition.name}' for field '${field.displayName}' in '${node.StepName}' must have an action.`
              };
              modelerValidationErrorsService.addError(goNodeData.key, error);
              errors.push(error);
            }
          });
        });
      });
    });
  }

  function getEnumValue(enumCollection: any, text: any) {
    if (isNaN(text)) {
      return enumCollection[
        Object.keys(enumCollection)
          .map((key) => enumCollection[key])
          .find(
            (e) => e.toString().toLowerCase() == text.toString().toLowerCase()
          )
      ];
    }
    return text;
  }

  function validateCustomValidation({ queryable }) {
    const allWorkflowNodes = getNodes();
    allWorkflowNodes.forEach((node) => {
      const Validationfields = (node.Card as IRunnerCard).formElements.filter(
        (f) => f.customValidation && f.customValidation.required
      );
      const goNodeData = queryable.goNodeData.getByIndex(
        'id',
        node.ModelerNodeId
      );

      Validationfields.forEach((f) => {
        const custvalidationObj = f.customValidation;
        const type: FormFieldType = f.type;
        switch (type) {
          case FormFieldType.SHORT_TEXT:
            if (
              !lodash.isNumber(custvalidationObj.maxLength) ||
              custvalidationObj.maxLength < 0 ||
              Math.floor(custvalidationObj.maxLength) !=
                custvalidationObj.maxLength
            ) {
              const error: IValidationError = {
                message: `Custom value of field '${f.displayName}' in '${node.StepName}' must be a whole number.`
              };
              modelerValidationErrorsService.addError(goNodeData.key, error);
              errors.push(error);
            }
            return;
          case FormFieldType.MULTISELECT_LIST:
            break;
          default:
            if (
              custvalidationObj.rule === undefined ||
              (custvalidationObj.rule as any) === ''
            ) {
              const error: IValidationError = {
                message: `Select validation rule  of field '${f.displayName}' in '${node.StepName}'.`
              };

              modelerValidationErrorsService.addError(goNodeData.key, error);
              errors.push(error);
              return;
            }

            if (custvalidationObj.valueOption === 'none') {
              if (!custvalidationObj.value) {
                const error: IValidationError = {
                  message: `Provide custom value of field '${f.displayName}' in '${node.StepName}'.`
                };

                modelerValidationErrorsService.addError(goNodeData.key, error);
                errors.push(error);
              }
            } else if (custvalidationObj.valueOption === 'previousValue') {
              const previousStep = allWorkflowNodes.find(
                (n) => n.ModelerNodeId === custvalidationObj.previousStepId
              );

              if (!previousStep) {
                const error: IValidationError = {
                  message: `The custom validation field setup for Step '${node.StepName}' field '${f.displayName}' is missing.`
                };
                modelerValidationErrorsService.addError(goNodeData.key, error);
                errors.push(error);
                return;
              } else {
                const defaultFormField = (
                  previousStep.Card as IRunnerCard
                ).formElements.find(
                  (pf) => pf.name === custvalidationObj.formFieldId
                );

                if (!defaultFormField) {
                  const error: IValidationError = {
                    message: `The custom validation field setup for Step '${node.StepName}' field '${f.displayName}' is missing.`
                  };
                  modelerValidationErrorsService.addError(
                    goNodeData.key,
                    error
                  );
                  errors.push(error);
                  return;
                }
              }
            } else if (custvalidationObj.valueOption === 'createdDate') {
              if (!custvalidationObj.stepCreatedDateOffset) {
                const error: IValidationError = {
                  message: `Provide step created date value of field '${f.displayName}' in '${node.StepName}'.`
                };
                modelerValidationErrorsService.addError(goNodeData.key, error);
                errors.push(error);
              }
            }
            break;
        }
      });
    });
  }

  function validateProcedures(node: IModelerNode, formElements: IFormField[]) {
    for (const field of formElements) {
      if (field.type !== FormFieldType.PROCEDURE) {
        continue;
      }
      if (!field.value || !guidService.isValidGuid(field.value)) {
        const error: IValidationError = {
          message: `SOP '${field.displayName}' in '${node.text}' has not been configured.`
        };
        errors.push(error);
      }
    }
  }

  function validateTable(
    firstNode: IModelerNode | null,
    node: IModelerNode,
    formElements,
    allCustomDbs
  ) {
    const tables = lodash.filter(formElements, function (formElement) {
      return formElement.type === 'table';
    });
    lodash.forEach(tables, function (table) {
      if (table.tableSchema != undefined && table.tableSchema !== null) {
        let rows = [];
        if (Array.isArray(table.tableSchema)) {
          rows = table.tableSchema;
        } else {
          rows = JSON.parse(table.tableSchema);
        }
        // [FLOW-5425] Flag to indicate whether any of the columns in the table are required.
        let hasRequiredColumn = false;

        // "rows" are not table rows its actually columns. TODO: Misleading name for rows here instead of columns
        lodash.forEach(rows, function (row) {
          // [FLOW-5425] If the column is set as required and the flag is not already set, then set it.
          if (row.isRequired && !hasRequiredColumn) {
            hasRequiredColumn = true;
          }
          if (row.type === TableCellType.DROPDOWN) {
            validateCustomDatabase(
              node,
              FieldDataSource.DATABASE,
              row.dbDataSource,
              table.displayName,
              row.header,
              allCustomDbs,
              errors
            );
          } else if (row.type === TableCellType.LOOKUP) {
            validateLookupField(
              node,
              row.lookupConfig,
              table.displayName,
              row.header,
              allCustomDbs,
              errors,
              firstNode
            );
          }
        });
        // [FLOW-5425] If table is required then it must have at-least one required column.
        if (
          table.validation != null &&
          table.validation.required &&
          !hasRequiredColumn
        ) {
          const error: IValidationError = {
            message: `'${table.displayName}' on '${node.text}' is set to required. At least one column of that table must also be set to required.`
          };
          errors.push(error);
        }
      } else {
        const error: IValidationError = {
          message: `Table schema in '${node.text}' for '${table.displayName}' is missing.`
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
    });
  }

  function validateOptions(
    node,
    formElements,
    listType,
    displayname,
    allCustomDbs
  ) {
    const lists = lodash.filter(formElements, function (formElement) {
      return formElement.type === listType;
    });

    lodash.forEach(lists, function (list) {
      const listValues = lodash.map(list.options, 'value');
      const hasDuplicates =
        lodash.uniq(listValues).length !== listValues.length;
      if (hasDuplicates) {
        const error: IValidationError = {
          message: `${displayname} list option values in '${node.text}' [${list.displayName}] must be unique.`
        };
        modelerValidationErrorsService.addError(node.key, error);
        errors.push(error);
      }
      validateCustomDatabase(
        node,
        list.dataSource,
        list.dbDataSource,
        displayname,
        list.displayName,
        allCustomDbs,
        errors
      );
    });
  }

  function validateInstructionField(
    formElement,
    context,
    workflowNode,
    modelNode,
    errors
  ) {
    const instructionValue = formElement.value;
    if (instructionValue != null) {
      const invalidVariables = getInvalidTemplateVariablesForPriorNodes(
        instructionValue,
        workflowNode,
        context.workflowNodes,
        context.allModelNodes,
        context.allLinks
      );
      if (invalidVariables) {
        invalidVariables.forEach((variable) => {
          const error = {
            message: `'${workflowNode.StepName}' contains invalid step field variable '${variable}' in instruction field 'step.${workflowNode.StepName}.${formElement.displayName}'`
          };
          modelerValidationErrorsService.addError(modelNode.key, error);
          errors.push(error);
        });
      }
    }
  }

  function validateLookupField(
    node: IModelerNode,
    lookupConfig,
    displayName,
    fieldDisplayName,
    allCustomDbs,
    errors,
    firstNode: IModelerNode | null
  ) {
    if (isValueEmpty(lookupConfig)) {
      const error = {
        message: `${displayName} ${fieldDisplayName} in '${node.text}' is not configured.`
      };
      errors.push(error);
      modelerValidationErrorsService.addError(node.key, error);
      return;
    }

    if (isValueEmpty(lookupConfig.dbName)) {
      const error = {
        message: `${displayName} ${fieldDisplayName} in '${node.text}' does not have a Database selected.`
      };
      errors.push(error);
      modelerValidationErrorsService.addError(node.key, error);
    } else if (
      !isValidCustomDatabaseReference(allCustomDbs, lookupConfig.dbName)
    ) {
      const error = {
        message:
          `${displayName} ${fieldDisplayName} in '${node.text}' references ` +
          `database ${lookupConfig.dbName} which does not exist.`
      };
      errors.push(error);
      modelerValidationErrorsService.addError(node.key, error);
    }

    if (
      !isLookupDisplayOrQueryValueValid(
        lookupConfig.displayValue,
        lookupConfig.value,
        lookupConfig.queryValue
      )
    ) {
      const error = {
        message: `${displayName} ${fieldDisplayName} in '${node.text}' does not have a Display Column selected.`
      };
      errors.push(error);
      modelerValidationErrorsService.addError(node.key, error);
    }

    if (
      !isLookupDisplayOrQueryValueValid(
        lookupConfig.queryValue,
        lookupConfig.value,
        lookupConfig.displayValue
      )
    ) {
      const error = {
        message: `${displayName} ${fieldDisplayName} in '${node.text}' does not have a Query Column selected.`
      };
      errors.push(error);
      modelerValidationErrorsService.addError(node.key, error);
    }

    if (isValueEmpty(lookupConfig.value)) {
      const error = {
        message: `${displayName} ${fieldDisplayName} in '${node.text}' does not have a Previous Field selected.`
      };
      errors.push(error);
      modelerValidationErrorsService.addError(node.key, error);
    } else if (
      (lookupConfig.value === LookupSpecialFields.CurrentActorFullName ||
        lookupConfig.value === LookupSpecialFields.CurrentActorEmail) &&
      ((node.taskType === ActivityTaskType.PUBLIC_FORM &&
        node.publicFormType === PublicFormType.Public) ||
        node.taskType === ActivityTaskType.EXTERNAL_USER_TASK)
    ) {
      const currentActorOptionText =
        lookupConfig.value === LookupSpecialFields.CurrentActorFullName
          ? LookupSpecialFieldLabels.CurrentActorFullNameText
          : LookupSpecialFieldLabels.CurrentActorEmailText;

      const error = {
        message:
          `${displayName} ${fieldDisplayName} in '${node.text}' has '${currentActorOptionText}' selected` +
          ` as the Previous Field, this value is not valid on Public-type Public Forms or External User Tasks.`
      };
      errors.push(error);
      modelerValidationErrorsService.addError(node.key, error);
    } else if (
      (lookupConfig.value === LookupSpecialFields.InitiatorFullName ||
        lookupConfig.value === LookupSpecialFields.InitiatorEmail) &&
      firstNode?.taskType === ActivityTaskType.PUBLIC_FORM &&
      firstNode?.publicFormType === PublicFormType.Public
    ) {
      const initiatorLabel =
        lookupConfig.value === LookupSpecialFields.InitiatorFullName
          ? LookupSpecialFieldLabels.InitiatorFullName
          : LookupSpecialFieldLabels.InitiatorEmail;
      const error = {
        message:
          `${displayName} ${fieldDisplayName} in '${node.text}' has '${initiatorLabel}' selected as the` +
          ` Previous Field, this value is not valid in FlowModels that start with a Public-type Public Form.`
      };
      errors.push(error);
      modelerValidationErrorsService.addError(node.key, error);
    }
  }

  function validateRecaptchaField(
    formElement,
    workflowNode,
    modelNode,
    errors
  ) {
    if (!workflowNode.IsPublicForm) {
      const error = {
        message: `'${workflowNode.StepName}' contains invalid field '${formElement.displayName}', this field is only valid on Public Forms.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }

    if (workflowNode.Card.formElements.at(-1) !== formElement) {
      const error = {
        message: `'${workflowNode.StepName}' contains invalid field '${formElement.displayName}'. The reCAPTCHA field must be placed at the end of the form.`
      };
      modelerValidationErrorsService.addError(modelNode.key, error);
      errors.push(error);
    }
  }

  function validateCustomDatabase(
    node,
    dataSource: FieldDataSource,
    dbDataSource,
    displayName,
    fieldDisplayName,
    allCustomDbs,
    errors
  ) {
    if (isValueEmpty(dataSource) || dataSource !== FieldDataSource.DATABASE) {
      return;
    }

    if (isValueEmpty(dbDataSource)) {
      const error = {
        message: `${displayName} data source config in ${node.text} for ${fieldDisplayName} is missing.`
      };

      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
      return;
    }

    if (isValueEmpty(dbDataSource.dbName)) {
      const error = {
        message: `${displayName} data source database name in ${node.text} for ${fieldDisplayName} is missing.`
      };

      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
    if (
      isValueEmpty(dbDataSource.displayValue) ||
      dbDataSource.displayValue === flowinglyConstants.EMPTY_NAME
    ) {
      const error = {
        message: `${displayName} data source display value in ${node.text} for ${fieldDisplayName} is missing.`
      };

      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }

    if (
      !isValueEmpty(dbDataSource.dbName) &&
      !isValidCustomDatabaseReference(allCustomDbs, dbDataSource.dbName)
    ) {
      const error = {
        message: `${displayName} data source database name ${dbDataSource.dbName} in ${node.text} for ${fieldDisplayName} is not exist.`
      };

      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }

    if (dbDataSource.filters[0]) {
      if (
        !isValueEmpty(dbDataSource.filters[0].column) ||
        !isValueEmpty(dbDataSource.filters[0].operation) ||
        !isValueEmpty(dbDataSource.filters[0].value)
      ) {
        validateCustomDatabaseFilters(
          displayName,
          node,
          dbDataSource,
          fieldDisplayName,
          errors
        );
      }
    }
  }

  function validateWorkflowPassXssCheck(workflow) {
    return $http.post(
      appConfig.apiBaseUrl + 'modeler/validateWorkflowPassXssCheck',
      workflow
    );
  }

  function validateStepRules(formElements, allModelNodes) {
    if (allModelNodes) {
      lodash.forEach(allModelNodes, function (node) {
        if (
          node.rules !== null ||
          node.rules !== undefined ||
          node.rules !== ''
        ) {
          lodash.forEach(node.rules, function (rule, ruleIndex) {
            const isvoid = function (x) {
              return typeof x === 'undefined' || x === null;
            };
            const ruleName = rule.ruleName || `Rule ${ruleIndex + 1}`;
            // Trigger Condition validation
            if (
              !isvoid(rule.isEnableRule) &&
              rule.isEnableRule === true &&
              (isNaN(rule.condition) || isValueEmpty(rule.condition))
            ) {
              const error: IValidationError = {
                message: `'${node.text}' Rule '${ruleName}' is missing its Condition. Select a Condition for each Rule.`
              };
              modelerValidationErrorsService.addError(node.key, error);
              errors.push(error);
            }
            // Trigger Form Field validation
            if (
              !isvoid(rule.isEnableRule) &&
              rule.isEnableRule === true &&
              !isvoid(rule.triggerFieldId)
            ) {
              const triggerFieldIdExists = [];
              lodash.filter(formElements, function (item) {
                if (!isvoid(item)) {
                  if (!isvoid(item.Card)) {
                    if (!isvoid(item.Card.formElements)) {
                      lodash.filter(
                        item.Card.formElements,
                        function (formElement) {
                          if (formElement.name === rule.triggerFieldId) {
                            triggerFieldIdExists.push({
                              id: formElement.name,
                              name: rule.triggerFieldId
                            });
                          }
                        }
                      );
                    }
                  }
                }
              });

              if (
                triggerFieldIdExists === undefined ||
                triggerFieldIdExists === null ||
                triggerFieldIdExists.length < 1
              ) {
                const error: IValidationError = {
                  message: `'${node.text}' Rule '${ruleName}' is missing its Form Field. Select a Form Field for each Rule.`
                };
                modelerValidationErrorsService.addError(node.key, error);
                errors.push(error);
              }
            } else if (
              !isvoid(rule.isEnableRule) &&
              rule.isEnableRule === true &&
              isValueEmpty(rule.triggerFieldId)
            ) {
              const error: IValidationError = {
                message: `'${node.text}' Rule '${ruleName}' is missing its Form Field. Select a Form Field for each Rule.`
              };
              modelerValidationErrorsService.addError(node.key, error);
              errors.push(error);
            }
            // Action validation

            lodash.forEach(rule.actions, function (action) {
              //Action Event
              if (
                !isvoid(rule.isEnableRule) &&
                rule.isEnableRule === true &&
                isNaN(action.action)
              ) {
                const error: IValidationError = {
                  message: `'${node.text}' Rule '${ruleName}' is missing an Action Event. Select an Event for each Rule Action.`
                };
                modelerValidationErrorsService.addError(node.key, error);
                errors.push(error);
              } else if (
                !isvoid(rule.isEnableRule) &&
                rule.isEnableRule === true &&
                isValueEmpty(action.action)
              ) {
                const error: IValidationError = {
                  message: `'${node.text}' Rule '${ruleName}' is missing an Action Event. Select an Event for each Rule Action.`
                };
                modelerValidationErrorsService.addError(node.key, error);
                errors.push(error);
              }

              //Action Step
              if (
                !isvoid(rule.isEnableRule) &&
                rule.isEnableRule === true &&
                !isValueEmpty(action.applyToNodeId)
              ) {
                const applyToNodeExists = [];
                lodash.filter(allModelNodes, function (item) {
                  if (
                    item.id === action.applyToNodeId &&
                    item.actorType === 'dynamic'
                  ) {
                    applyToNodeExists.push({
                      id: item.id,
                      name: action.applyToNodeId
                    });
                  }
                });

                if (
                  applyToNodeExists === undefined ||
                  applyToNodeExists === null ||
                  applyToNodeExists.length < 1
                ) {
                  const error: IValidationError = {
                    message: `'${node.text}' Rule '${ruleName}' is missing an Action Step. Select a Step for each Rule Action.`
                  };
                  modelerValidationErrorsService.addError(node.key, error);
                  errors.push(error);
                }
              } else if (
                !isvoid(rule.isEnableRule) &&
                rule.isEnableRule === true &&
                isValueEmpty(action.applyToNodeId)
              ) {
                const error: IValidationError = {
                  message: `'${node.text}' Rule '${ruleName}' is missing an Action Step. Select a Step for each Rule Action.`
                };
                modelerValidationErrorsService.addError(node.key, error);
                errors.push(error);
              }
            });
          });
        }
      });
    }
  }

  function isLookupDisplayOrQueryValueValid(
    valueToCheck,
    lookupValue,
    lookupColumn
  ) {
    if (!isValueEmpty(valueToCheck)) {
      return true;
    }

    if (isLookupSpecialFieldWithOptionalDisplayAndQueryPair(lookupValue)) {
      return isValueEmpty(lookupColumn);
    }

    return false;
  }

  function isLookupSpecialFieldWithOptionalDisplayAndQueryPair(
    lookupValue: string
  ): boolean {
    return (
      lookupValue === LookupSpecialFields.CurrentActorFullName ||
      lookupValue === LookupSpecialFields.CurrentActorEmail ||
      lookupValue === LookupSpecialFields.InitiatorFullName ||
      lookupValue === LookupSpecialFields.InitiatorEmail
    );
  }

  function isValidCustomDatabaseReference(allCustomDbs, dbName) {
    return allCustomDbs.some(
      (db) => db.physicalTableName === dbName || db.name === dbName
    );
  }

  function isValueEmpty(valueToCheck) {
    return (
      valueToCheck === undefined || valueToCheck === null || valueToCheck === ''
    );
  }

  function validateCustomDatabaseFilters(
    displayName,
    node,
    dbDataSource,
    fieldDisplayName,
    errors
  ) {
    //atrocious code, not extensible. We actually wanted to use Object.values but we're having problems using it inside typescript so there...
    if (isValueEmpty(dbDataSource.filters[0].column)) {
      const error = {
        message: `${displayName} Column filter in ${node.text} for ${fieldDisplayName} is missing.`
      };

      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }

    if (isValueEmpty(dbDataSource.filters[0].operation)) {
      const error = {
        message: `${displayName} Operation filter in ${node.text} for ${fieldDisplayName} is missing.`
      };

      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }

    if (isValueEmpty(dbDataSource.filters[0].value)) {
      const error = {
        message: `${displayName} Value filter in ${node.text} for ${fieldDisplayName} is missing.`
      };

      modelerValidationErrorsService.addError(node.key, error);
      errors.push(error);
    }
  }

  function checkForDivergePathsWithoutSteps(
    nodes: IModelerNode[],
    errors: IValidationError[],
    linksByFromKey: Map<number, IModelerNodeLink[]>
  ) {
    const diverges = nodes.filter(
      (node) => node.category === NodeCategory.DIVERGE_GATEWAY
    );
    diverges.forEach((diverge) => {
      const fromLinks = linksByFromKey.get(diverge.key) || [];
      fromLinks.forEach((link) => {
        while (link) {
          const nextNode = nodes.find((node) => node.key === link.to);
          if (
            nextNode.taskType !== undefined ||
            nextNode.category === NodeCategory.EXCLUSIVE_GATEWAY ||
            nextNode.category === NodeCategory.COMPONENT
          ) {
            return;
          } else if (nextNode.category === NodeCategory.CONVERGE_GATEWAY) {
            const error: IValidationError = {
              message:
                `No Step found on the path between '${diverge.text}' and '${nextNode.text}'. ` +
                'You must add a Step to this path.'
            };
            modelerValidationErrorsService.addError(link.from, error);
            errors.push(error);
            return;
          } else if (nextNode.category === NodeCategory.DIVERGE_GATEWAY) {
            const error: IValidationError = {
              message:
                `No Step found on the path between '${diverge.text}' and '${nextNode.text}'. ` +
                `Remove '${nextNode.text}' and start its paths directly from '${diverge.text}' instead.`
            };
            modelerValidationErrorsService.addError(link.from, error);
            errors.push(error);
            return;
          }

          link = linksByFromKey.get(nextNode.key)?.[0];
        }
      });
    });
  }

  function validatePublicFormSettings(
    node: IModelerNode,
    errors: IValidationError[]
  ) {
    if (
      (node.publicFormType === PublicFormType.Public &&
        !appConfig.enablePublicFormsAnonymous) ||
      (node.publicFormType === PublicFormType.Intranet &&
        !appConfig.enablePublicFormsSignIn)
    ) {
      const error: IValidationError = {
        message: `${
          PublicFormTypeLabels[node.publicFormType]
        } Forms are disabled, ${
          node.text
        } must be changed to a different Step Type`
      };
      errors.push(error);
    }
  }
}

export type ModelerValidationServiceType = ReturnType<
  typeof modelerValidationService
>;
